/**
 * Top-level app. Renders the canvas of layout items, chrome (Header,
 * modals), and installs the boot-time sync/poll effects that keep the app
 * live against OPBridge (clock, engines-state, mixer, consumer WS, project/
 * matrix/waves doc sync). PWA-only since 2026-08-20 (DEC-001/Sprint Session
 * 2) — talks to OPBridge exclusively over HTTP/WS via `opbridge/`; the
 * legacy Node bridge this file used to also connect to is deleted.
 */

import { paneFromStores, closePaneStores } from './navigate/panes.ts';
import { recordActivity } from './navigate/recentActivity.ts';
import { installBackGuard, onBack } from './navigate/backButton.ts';
import { installLeaveGuard } from './navigate/leaveGuard.ts';
import { installBrowserGuards } from './navigate/browserGuards.ts';
import { installResume } from './navigate/resume.ts';
import { DropCatch } from './components/DropCatch.tsx';
import { createEffect, createMemo, createSignal, on, onCleanup, onMount, untrack, For, Index, Show } from 'solid-js';
import { Portal } from 'solid-js/web';
import { installDevHooks } from './devHooks.ts';
import { Header } from './components/Header.tsx';
import { InstrumentIconSprite } from './components/InstrumentIcon.tsx';
import { installPressFeel } from './layout/pressFeel.ts';
installPressFeel();
import { SineLfo } from './components/SineLfo.tsx';
import { FireButton } from './components/FireButton.tsx';
import { ButtonGrid, stopEditingCells } from './components/ButtonGrid.tsx';
import { StatusLed } from './components/StatusLed.tsx';
import { SequencerGrid } from './components/SequencerGrid.tsx';
import { EditableItem } from './components/EditableItem.tsx';
import { ProjectBrowser } from './components/ProjectBrowser.tsx';
import { Navigate } from './components/Navigate.tsx';
import { navigateOpen, openNavigate, openNavigateAt, navigatePane, setNavigatePane, toggleNavigate } from './navigate/store.ts';
import {
  navigateDocked, dockChromeShown, setDockCapable, measureDockCapable, canvasActualSize,
  DOCK_SIDE_W, DOCK_HEAD_H,
} from './navigate/dock.ts';
import { pageManagerOpen } from './layout/pageManagerStore.ts';
import { ScaledCanvas } from './components/ScaledCanvas.tsx';
import { InspectorPanel } from './components/InspectorPanel.tsx';
import { StreamDeckButton } from './components/StreamDeckButton.tsx';
import { MacroItemTile } from './components/MacroItemTile.tsx';
import { Fader } from './components/Fader.tsx';
import { Radial } from './components/Radial.tsx';
import { Label } from './components/Label.tsx';
import { PresetChange } from './components/PresetChange.tsx';
import { SnapshotChange } from './components/SnapshotChange.tsx';
import { RigStateButton } from './components/RigStateButton.tsx';
import { rigStateManagerOpen } from './rigState/store.ts';
import { Display } from './components/Display.tsx';
import { XYPad } from './components/XYPad.tsx';
import { TimelineLauncher } from './components/TimelineLauncher.tsx';
import { Piano } from './components/Piano.tsx';
import { Transpose } from './components/Transpose.tsx';
import { Capo } from './components/Capo.tsx';
import { RecQuant } from './components/RecQuant.tsx';
import { AbletonRow } from './components/AbletonRow.tsx';
import { SceneCopy } from './components/SceneCopy.tsx';
import { WavesAutotune } from './components/WavesAutotune.tsx';
import { Mixer } from './components/Mixer.tsx';
import { WavesConflictModal } from './components/WavesConflictModal.tsx';
import { ToastHost } from './components/ToastHost.tsx';
import { CrashPrompt } from './components/CrashPrompt.tsx';
import { installReportOutbox } from './diag/reportQueue.ts';
import { installHelpDeskBadge } from './diag/helpDesk.ts';
import { pushToast } from './ui/toast.ts';
import { preflightOpen, runAndStore } from './performance/preflightStore.ts';
import { performanceMode, installPerformanceModeSync } from './opbridge/performanceMode.ts';
import { ItemPalette } from './components/ItemPalette.tsx';
import { MacrosEditor } from './components/MacrosEditor.tsx';
import { MatrixViewer } from './components/MatrixViewer.tsx';
import { ConfirmChoice } from './components/ConfirmChoice.tsx';
import { TimelineEditor } from './components/TimelineEditor.tsx';
import { TimelineV2 } from './components/TimelineV2.tsx';
import { settingsOpen, connectOpen, connectedDevicesOpen, trafficMonitorOpen, openConnect, barPosition, activeSkin } from './settings/store.ts';
import { TrafficMonitorPanel } from './components/TrafficMonitorPanel.tsx';
import { setShellPortal } from './layout/shellPortal.ts';
import { ItemContextMenu } from './components/ItemContextMenu.tsx';
import { itemMenu, openItemMenu, closeItemMenu, viewportToCanvas } from './layout/itemMenu.ts';
import { stageDim } from './performance/store.ts';
import { acquireWakeLock, releaseWakeLock, wakeLockMode } from './performance/wakeLock.ts';
import { lintProject } from './performance/lint.ts';
import { startEnginesStatePoll } from './opbridge/enginesStatePoll.ts';
import { startToggleStatePoll } from './opbridge/toggleStatePoll.ts';
import { startObsScenePoll } from './obs/scenePoll.ts';
import { startProgramPoll } from './program/programPoll.ts';
import { startAbletonDevicePoll } from './ableton/devicePoll.ts';
import { startTimelineStatePoll } from './timeline/statePoll.ts';
import { startCanvasLights } from './timeline/canvasLights.ts';
import { startMotionCaptureListener } from './timeline/motionCapture.ts';
import { startCountIn } from './timeline/countIn.ts';
import { installTimelineAutoPush } from './timeline/autoSync.ts';
import { startPeerPwaSockets, stopPeerPwaSockets } from './opbridge/peerPwaSockets.ts';
import { startClockPoll, stopClockPoll, pokeClockPoll, clockSourceLabel, type ClockSnapshot } from './opbridge/clockPoll.ts';
import { startMidiInPoll, stopMidiInPoll } from './opbridge/midiInPoll.ts';
import { startInboundMidiFeed } from './midi/inboundFeed.ts';
import { startConsumerWs, stopConsumerWs, pokeConsumerWs, consumerRevoked } from './opbridge/consumerWs.ts';
import { consumerWsConnected } from './opbridge/consumerWs.ts';
import { startBlackBox, stopBlackBox } from './diag/blackBox.ts';
import { BridgeLostBanner } from './components/BridgeLostBanner.tsx';
import { startMixerStatusPoll, stopMixerStatusPoll, pokeMixerStatusPoll } from './mixer/statusPoll.ts';
import { startInheritanceDrainer, stopInheritanceDrainer } from './opbridge/inheritanceDrainer.ts';
import { requestPersistentStorage } from './storage/persist.ts';
import { installStreamDeckPrewarm } from './opbridge/streamDeckActions.ts';
import { installFixtureSync } from './fixtures/bridgeSync.ts';
import { installProfileSync } from './fixtures/profileSync.ts';
import { installLightingLiveState } from './lighting/liveState.ts';
import { LightColorButton } from './components/LightColorButton.tsx';
import { LightLevelFader } from './components/LightLevelFader.tsx';
import { LightFxButton } from './components/LightFxButton.tsx';
import { BlackoutButton } from './components/BlackoutButton.tsx';
import { OpVisualTrigger } from './components/OpVisualTrigger.tsx';
import { OpVisualGrid } from './components/OpVisualGrid.tsx';
import { AudioPads } from './components/AudioPads.tsx';
import { lightsManagerOpen } from './components/LightsManager.tsx';
import { HelixPresetPickerHost, helixPickerOpen } from './components/HelixPresetPicker.tsx';
import { anyConnectedReactive, bridges, getConnection } from './opbridge/store.ts';
import {
  practiceBpm, practiceIsPlaying, practiceDownbeatEpoch, practiceQuantum,
  practiceSigNum, practiceSigDen,
} from './clock/practiceClock.ts';
import { installGlobalUndoRedoHandler } from './undo/history.ts';
import { matrixViewerOpen, closeMatrixViewer, matrixCcLabel } from './matrix/store.ts';
import { macroLibrary, macrosEditorOpen, closeMacrosEditor } from './macros/store.ts';
import { timelineEditorOpen, timelineEditorKind, installTimelineOwnership } from './timeline/store.ts';
import { pendingWavesConflict } from './autotune/conflict.ts';
import { sequencerLibrary } from './sequencer/store.ts';
import { installPedalRebind, installPedalNodeMigration } from './pedals/rebind.ts';
import { installBridgeAffinityMigration } from './layout/bridgeAffinityMigration.ts';
import { installSetlistPrime } from './setlist/poll.ts';
import { installProjectSync } from './layout/projectSync.ts';
import { installDropTuningCarryOver } from './transpose/session.ts';
import { installMatrixSync } from './matrix/matrixSync.ts';
import { installRigStateSync } from './rigState/rigStateSync.ts';
import { installMidiTriggerRuleSync } from './midiTrigger/ruleSync.ts';
import { installModShareSync } from './modShare/sync.ts';
import { createSmoothClock, setSmoothClockDebug } from './clock/smoothClock.ts';
import {
  layoutItems, selectedItem, selectItem, isEditMode, uiMode, toggleEditMode,
  hasItemClipboard, pasteItems, disarmItemMove, moveArmedItemId,
  selectedItemIds, setSelectedItems, clearSelection, inspectorItem,
  pickButtonForDisplayId, cancelPickButton,
  dragOverlay, nudgeSelection, setSpawnPoint, announceAdded,
} from './layout/store.ts';
import { deviceLabel, reserveOf, fitScale, READABLE_SCALE_FLOOR } from './layout/devices.ts';
import { DOCK_RESERVE_W, dockDecision, inspectorDocked, setInspectorDocked } from './layout/inspectorDock.ts';
import { maybeAutoFit, maybeRecordReserve } from './layout/autoFit.ts';
import { measureViewportBox, isStandalone } from './layout/trueViewport.ts';
import { startWatchdog, watchJob } from './diag/watchdog.ts';
import { installHelixAutoLoad } from './helix/library.ts';
import {
  activeProject,
  activeProjectId,
  activePage,
  activeCanvasSize,
  pageAnimDirection,
  projects,
  stepPage,
  addPage,
  renamePage,
  projectHistory,
} from './layout/projectStore.ts';

export function App() {
  installDevHooks();
  // Helix names: OPBridge's cache (a GET, never the pedal) on every connect
  // while the project has a preset tile, so tiles show the latest read.
  installHelixAutoLoad(
    createMemo(() => anyConnectedReactive()),
    createMemo(() => !!activeProject()?.pages.some((pg) => pg.items.some((it) => it.kind === 'presetChange'))),
  );
  let viewportFitRef: HTMLDivElement | undefined;
  let shellElRef: HTMLDivElement | undefined;
  // Shell-wide scale: the entire .shell (header + canvas + footer) is a
  // fixed-pixel iPad-sized box; this signal CSS-scales it to fit the
  // viewport while preserving aspect. On the actual iPad ≈ 1.0; on
  // desktop typically <1 with letterboxing around the whole shell.
  const [shellScale, setShellScale] = createSignal(1);
  // How far the shell slides left while the Inspector is docked beside it
  // (layout/inspectorDock.ts), and a short window in which that slide and
  // the scale change animate - only then, so a resize or a project switch
  // still lands at once and every measurement after it is final.
  const [shellShift, setShellShift] = createSignal({ x: 0, y: 0 });
  const [dockAnim, setDockAnim] = createSignal(false);
  let dockAnimTimer: number | undefined;
  onCleanup(() => { if (dockAnimTimer !== undefined) clearTimeout(dockAnimTimer); });
  // The frame when NO project is open (first run, or every project deleted).
  // There is no canvas to fit, so the frame is the screen, capped at the old
  // 1180x820 fallback: unchanged on an iPad or desktop, but on a phone it is
  // the phone's own width, so the forced New-project dialog (sized in % of
  // this frame) fits instead of running off both edges (OPApps UX audit A3,
  // 2026-09-28, reproduced at 390px: frame 1180 wide at scale 1, dialog
  // spanning -165..555).
  const [bareFrame, setBareFrame] = createSignal({ w: 1180, h: 820 });
  // Boots false — the practice clock defaults to stopped (no live clock yet),
  // and the practice effect keeps this in sync; a live transport sets it true.
  const [isPlaying, setIsPlaying] = createSignal(false);
  // ProjectBrowser open/closed. On first run (no projects) we force it open
  // and non-dismissible — the user must create a project before doing anything.
  const [browserOpen, setBrowserOpen] = createSignal(projects().length === 0);
  // Which of ProjectBrowser's views it opens into. Navigate's "+ New" jumps
  // straight to the creation form, and its "Getting started" card to the
  // welcome view - read once at mount (the browser fully unmounts/remounts on
  // each open via the <Show> below, so a stale value can't linger).
  const [browserInitialView, setBrowserInitialView] = createSignal<'list' | 'new' | 'welcome'>('list');
  // First run shows a welcome view before the New-project form (OPApps UX
  // audit A4, operator 2026-09-28). Session-only: once the user has moved on
  // (Start a project, or Connect), a remount goes straight to the form.
  const [welcomeDone, setWelcomeDone] = createSignal(false);
  const [paletteOpen, setPaletteOpen] = createSignal(false);
  // Fit notice dismissal. Session state, never persisted: a new session on a
  // different device is a different answer to "does this fit", and a
  // remembered dismissal would silently suppress the warning on the one
  // machine where it matters.
  const [fitNoticeDismissed, setFitNoticeDismissed] = createSignal(false);

  // The InspectorPanel is a document-level sibling of .shell (it sits above
  // the shell's stacking context), so any shell-mounted modal renders UNDER
  // it. Suppress the inspector whenever a modal is up: the modal owns focus
  // and its controls must stay clickable. (Regression this fixes: the Waves
  // conflict prompt, triggered from the inspector's channel select, was
  // unreachable behind the inspector once modals moved inside the shell.)
  // Navigate and the page manager joined 2026-09-30: both were component-local
  // signals this memo could not see, so an open Inspector covered Navigate's
  // right edge (PANIC, close) and the page manager's list.
  const anyModalOpen = createMemo(() =>
    settingsOpen() || connectOpen() || connectedDevicesOpen() || trafficMonitorOpen() || paletteOpen() ||
    macrosEditorOpen() || browserOpen() || !activeProject() ||
    matrixViewerOpen() || timelineEditorOpen() ||
    preflightOpen() || rigStateManagerOpen() || lightsManagerOpen() || helixPickerOpen() ||
    navigateOpen() || pageManagerOpen() ||
    !!pendingWavesConflict(),
  );
  // The dock follows the item being INSPECTED, not the panel being visible:
  // a modal opened from the Inspector (Lights, the Helix picker) hides the
  // panel for a moment, and the canvas sliding back and forth behind it each
  // time would be motion for nothing.
  const inspectorWanted = createMemo(() => !!inspectorItem());

  // Moving the open Inspector from one item to another (operator,
  // 2026-10-10: "when clicking between items the inspector flashes off and
  // back on again, just needs a smooth crossfade"). The flash was the panel
  // being torn down and rebuilt (fixed in selectItem); what is left is the
  // contents fading up as the new item lands, the panel's frame never moving.
  //
  // NOT the View Transitions API, tried first: while a view transition runs,
  // the browser sends every pointer event to <html>, so a click in the 160ms
  // after a switch did nothing at all - right_click_copy_and_inspector_follow
  // caught the context menu never opening. An opacity animation blocks no
  // input. Reduced motion: it just swaps.
  createEffect(on(() => (anyModalOpen() ? null : inspectorItem()?.id), (id, prev) => {
    if (!id || !prev || id === prev) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    queueMicrotask(() => {
      document.querySelector('.inspector-panel .inspector-scroll')
        ?.animate([{ opacity: 0.25 }, { opacity: 1 }], { duration: 180, easing: 'ease-out' });
    });
  }));

  // EVERY SECTION OPENS INSIDE NAVIGATE (2026-10-05, app-study.html). The
  // sections' own open switches stay the source of truth - the OPBridge pill,
  // the Inspector's links, the page pills and first run still flip them - and
  // whichever is on opens Navigate at that section. Closing Navigate turns
  // them all off, so nothing is left "open" behind it. See navigate/panes.ts.
  //
  // It tracks the SWITCHES ONLY. Reading navigateOpen() here made it re-run
  // on close, ahead of the effect below that turns the switches off: it saw
  // the section's switch still on and opened Navigate again, so neither Esc
  // nor "Back to the stage" could leave a section that has a switch.
  createEffect(on(paneFromStores, (want) => {
    if (!want) return;
    if (untrack(navigatePane) !== want) setNavigatePane(want);
    if (!untrack(navigateOpen)) openNavigateAt(want);
  }));
  createEffect(on(navigateOpen, (open) => { if (!open) closePaneStores(); }, { defer: true }));
  // "Jump back in" in Navigate (2026-10-05): a control opened in the
  // Inspector is the last thing touched, with the page it is on.
  createEffect(on(() => inspectorItem()?.id, (id) => {
    const it = inspectorItem();
    const pg = activePage();
    if (!id || !it || !pg) return;
    const name = 'name' in it && typeof it.name === 'string' && it.name.trim() ? it.name.trim() : it.kind;
    recordActivity({ kind: 'control', id, title: name, pageId: pg.id, pageName: pg.name, ...(activeProjectId() ? { projectId: activeProjectId()! } : {}) });
  }, { defer: true }));
  // Canvas right-click menu (Edit Page / + Item) - the page-level actions
  // that used to live as buttons in the top bar. Opens at the cursor on a
  // right-click over empty canvas (item right-clicks stopPropagation, so
  // they keep their own inspector behaviour). Null = closed.
  const [canvasMenu, setCanvasMenu] = createSignal<{ x: number; y: number } | null>(null);
  /** "Rename this page" turned its row into a name box (2026-10-16: a page
   *  was renamed only in the Pages manager). */
  const [renamingPageInMenu, setRenamingPageInMenu] = createSignal(false);
  function closeCanvasMenu() { setCanvasMenu(null); setRenamingPageInMenu(false); }
  function onCanvasContextMenu(ev: MouseEvent) {
    // Suppress the native menu + don't double-handle at the viewport-fit
    // suppressor, regardless of mode. The menu itself is EDIT MODE ONLY
    // (widened 2026-08-11, operator: right-click/two-finger-tap on empty
    // space "shouldn't do anything in perform mode" - this used to open in
    // both Play and Edit; now the master Edit/Test toggle is the one way
    // in, not an incidental right-click on empty canvas. `!isEditMode()`
    // covers Performance mode too, same as it always did.
    //
    // Outside edit mode a MOUSE right-click opens it again (2026-10-16,
    // operator: on a desktop "in non edit mode, right click could open the
    // menu with edit page, add item etc"). Still nothing in performance mode
    // (the stage), and touch stays edit-only: the two-finger tap below keeps
    // its gate, and a touch-originated contextmenu (Android long-press) is
    // turned away here.
    ev.preventDefault();
    ev.stopPropagation();
    if (!isEditMode()) {
      if (performanceMode()) return;
      if ((ev as PointerEvent).pointerType === 'touch') return;
      // EMPTY canvas only. Out of edit mode an item lets its right-click
      // bubble (EditableItem's handler is edit-only), and a right-click on a
      // control must still do nothing there.
      if ((ev.target as HTMLElement | null)?.closest?.('.editable-item')) return;
    }
    closeItemMenu();
    setCanvasMenuAt(viewportToCanvas(ev.clientX, ev.clientY));
    setCanvasMenu({ x: ev.clientX, y: ev.clientY });
  }
  function onCanvasMenuKey(ev: KeyboardEvent) {
    if (ev.key === 'Escape' && canvasMenu()) closeCanvasMenu();
  }
  // Canvas-space point of the last canvas right-click / two-finger tap, so
  // the page menu's Paste drops the copy where the operator pointed rather
  // than at the top-left. Same convention the item menu uses.
  const [canvasMenuAt, setCanvasMenuAt] = createSignal<{ x: number; y: number } | null>(null);
  // The item menu, but only while its target still exists and editing is
  // legal. Guarding at the render site rather than trusting every mutation to
  // close it: an item can leave under the menu by routes the menu never sees
  // (a page switch, a project switch, an undo, a bridge-driven restore), and a
  // menu whose Delete points at a ghost is worse than no menu.
  const itemMenuTarget = createMemo(() => {
    const m = itemMenu();
    if (!m) return null;
    return layoutItems().some((it) => it.id === m.itemId) ? m : null;
  });
  onMount(() => window.addEventListener('keydown', onCanvasMenuKey));

  // Ctrl/Cmd+K opens Navigate with its search focused, from anywhere - the
  // pro path to any destination in two keystrokes. Once Navigate is open its
  // own handler owns the shortcut (a second press closes it). Registered here
  // so it runs before Navigate's, and returns at once while Navigate is up.
  function onNavigateShortcut(ev: KeyboardEvent) {
    // \ hides and shows the docked Navigate (DEC-268), like the logo. Not
    // while typing, and not over a full-screen surface that owns the window.
    if (ev.key === '\\' && !ev.metaKey && !ev.ctrlKey && !ev.altKey && navigateDocked()
        && !typingInField(ev.target) && !timelineEditorOpen() && !matrixViewerOpen()) {
      ev.preventDefault();
      toggleNavigate();
      return;
    }
    if (navigateOpen()) return;
    if ((ev.key === 'k' || ev.key === 'K') && (ev.metaKey || ev.ctrlKey) && !ev.altKey && !ev.shiftKey) {
      ev.preventDefault();
      openNavigate({ focusSearch: true });
    }
  }
  onMount(() => window.addEventListener('keydown', onNavigateShortcut));
  onCleanup(() => window.removeEventListener('keydown', onNavigateShortcut));

  // ── TWO-FINGER TAP = RIGHT-CLICK (2026-07-30) ────────────────────────
  // Operator, from the iPad: "two finger tap on ipad for right click - i can't
  // add anything yet."
  //
  // That was literal, not a preference. DEC-089 moved "+ Item" and "Edit Page"
  // into the canvas right-click menu and retired the EDIT + Add buttons from
  // the top bar. iPad Safari fires `contextmenu` for a long-press on links and
  // images, but NOT on a plain div - so on the primary device the menu was
  // unreachable and **adding an item was impossible**. An authoring app whose
  // author gesture only exists on a mouse.
  //
  // Two fingers rather than long-press, as asked, and it is the better choice
  // here anyway: long-press is already taken (DEC-090's 2s hover-grab, and the
  // guard's press-and-hold), so overloading it would collide with gestures that
  // exist to prevent accidents.
  //
  // Deliberately routed to whatever a right-click AT THAT POINT would do:
  // on an item it opens the item context menu (Inspect / Move / Cut / Copy /
  // Paste / Duplicate / Delete — it used to select the item and pop the
  // inspector, changed 2026-08-09), on empty canvas it opens the page menu.
  // One gesture, same semantics as the mouse, rather than a second
  // touch-only vocabulary to remember. Keeping the two in step is the whole
  // reason this handler resolves the target itself instead of synthesising
  // a `contextmenu` event: iPad Safari never fires one on a plain div.
  let twoFinger: { x: number; y: number; t: number; moved: number } | null = null;
  const TWO_FINGER_TAP_MS = 600;   // longer and it is a hold, not a tap
  const TWO_FINGER_SLOP_PX = 24;   // further and it is a pinch or a drag

  // ── TWO- OR THREE-FINGER SWIPE = NEXT / PREVIOUS PAGE (2026-10-25) ───
  // Operator: "add two or 3 finger swipe on ipad to change page". Fingers moving left go to the next page, right to the
  // previous (as iOS turns pages), through the store's `stepPage` (the page pills' arrows use the same one), so it
  // stops at the first and last page and plays the usual slide.
  //
  // The rules keep it from firing by accident on stage. EVERY finger must START off the controls (touch events reach
  // this handler for fingers on items too, pointer capture or not): two fingers riding two faders sideways mid-song
  // must never flip the page. Every finger moves the same way and mostly sideways (a pinch moves them apart), and the
  // travel is far past the two-finger tap's 24 px slop, so a tap is never a swipe. It fires ONCE, as soon as the
  // travel is reached, and cancels the tap menu.
  let swipe: { starts: Map<number, { x: number; y: number }>; t: number; fired: boolean } | null = null;
  const SWIPE_MIN_PX = 80;          // each finger's sideways travel
  const SWIPE_MAX_MS = 700;         // slower and it is a drag, not a swipe
  const SWIPE_SIDEWAYS = 1.5;       // sideways travel at least this many times the vertical

  function startSwipe(ev: TouchEvent) {
    const n = ev.touches.length;
    if (n < 2 || n > 3) { swipe = null; return; }
    const onControl = Array.from(ev.touches).some((t) => (t.target as Element | null)?.closest?.('.editable-item'));
    if (onControl) { swipe = null; return; }
    // A finger added to the gesture restarts the measure, keeping the fingers already down where they started.
    const starts = new Map<number, { x: number; y: number }>();
    for (const t of Array.from(ev.touches)) starts.set(t.identifier, swipe?.starts.get(t.identifier) ?? { x: t.clientX, y: t.clientY });
    swipe = { starts, t: performance.now(), fired: false };
  }
  function moveSwipe(ev: TouchEvent): boolean {
    const g = swipe;
    if (!g || g.fired || ev.touches.length !== g.starts.size) return false;
    if (performance.now() - g.t > SWIPE_MAX_MS) { swipe = null; return false; }
    let dir = 0;
    for (const t of Array.from(ev.touches)) {
      const s = g.starts.get(t.identifier);
      if (!s) return false;
      const dx = t.clientX - s.x;
      const dy = t.clientY - s.y;
      if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) < Math.abs(dy) * SWIPE_SIDEWAYS) return false;
      const d = Math.sign(dx);
      if (dir !== 0 && d !== dir) return false;
      dir = d;
    }
    g.fired = true;
    twoFinger = null;
    stepPage(dir < 0 ? 1 : -1);
    return true;
  }

  function onCanvasTouchStart(ev: TouchEvent) {
    startSwipe(ev);
    if (ev.touches.length !== 2) { twoFinger = null; return; }
    const [a, b] = [ev.touches[0]!, ev.touches[1]!];
    twoFinger = {
      x: (a.clientX + b.clientX) / 2,
      y: (a.clientY + b.clientY) / 2,
      t: performance.now(),
      moved: 0,
    };
  }
  function onCanvasTouchMove(ev: TouchEvent) {
    if (moveSwipe(ev)) return;
    if (!twoFinger || ev.touches.length !== 2) return;
    const [a, b] = [ev.touches[0]!, ev.touches[1]!];
    const mx = (a.clientX + b.clientX) / 2;
    const my = (a.clientY + b.clientY) / 2;
    twoFinger.moved = Math.max(twoFinger.moved, Math.hypot(mx - twoFinger.x, my - twoFinger.y));
  }
  function onCanvasTouchEnd(ev: TouchEvent) {
    // The swipe ends when its last finger lifts.
    if (ev.touches.length === 0) swipe = null;
    const g = twoFinger;
    twoFinger = null;
    if (!g) return;
    if (performance.now() - g.t > TWO_FINGER_TAP_MS) return;
    if (g.moved > TWO_FINGER_SLOP_PX) return;
    // EDIT MODE ONLY (widened 2026-08-11 - was Show-mode-only before; the
    // operator's instruction was explicit: right-click/two-finger-tap on
    // empty space "shouldn't do anything in perform mode" either, matching
    // the mouse path (App.tsx's onCanvasContextMenu, same change) and the
    // item-menu gate below. No
    // preventDefault on the early return - `.canvas-placeholder`'s
    // `touch-action: none` already stops the browser doing anything default
    // with the gesture, so there is nothing left to suppress.
    if (!isEditMode()) return;
    ev.preventDefault();
    // Same routing a right-click would take at this point.
    const hit = document.elementFromPoint(g.x, g.y) as HTMLElement | null;
    const item = hit?.closest('.editable-item') as HTMLElement | null;
    if (item?.dataset.itemId) {
      closeCanvasMenu();
      openItemMenu(item.dataset.itemId, g.x, g.y);
      return;
    }
    closeItemMenu();
    setCanvasMenuAt(viewportToCanvas(g.x, g.y));
    setCanvasMenu({ x: g.x, y: g.y });
  }

  // ── PINCH-ZOOM ON MODALS, THE WEBKIT GESTURE-EVENT GAP (2026-08-09) ──
  // DEC-142's `touch-action: pan-x pan-y` correctly blocks pinch on Settings
  // (data-size="sheet") but NOT on the Navigate modal (data-size="full") -
  // reproduced live on the operator's iPad. Both share the identical CSS
  // rule; the only real difference is how much of the touch surface each
  // modal covers versus its `touch-action: none` scrim.
  //
  // Working theory, evidence-backed rather than guessed: Safari fires its
  // own proprietary `gesturestart`/`gesturechange` events for pinch,
  // independently of touch-action, and only `touch-action: none` reliably
  // suppresses them on this device - `pan-x pan-y` alone does not. Settings
  // "worked" only by accident: a real two-finger pinch there almost always
  // lands at least one finger on the surrounding (touch-action: none)
  // scrim, which blocks the whole gesture by ancestor intersection.
  // Navigate's near-fullscreen modal gives a centered pinch nowhere near
  // that scrim to land on, so nothing was ever actually suppressing it.
  //
  // Belt-and-suspenders fix: suppress the gesture events directly, scoped
  // to the SAME guarded-modal selector list DEC-142 already maintains in
  // CSS (skin-components.css, the "NO PINCH-ZOOM OVER A MODAL" block) -
  // keep the two lists in sync rather than inventing a second mechanism.
  // Kept in sync with skin-components.css's "NO PINCH-ZOOM OVER A MODAL"
  // list — extended 2026-08-09 to also cover the rect-anchored popovers
  // (canvas right-click menu; the ShapePicker popover went 2026-10-15) that aren't `.sk-modal`-family
  // but are just as pinch-able; reproduced live (pinch worked over the
  // canvas context menu, stopped once it was dismissed).
  const PINCH_GUARD_SELECTOR = '.sk-modal, .matrix-overlay, .macros-editor-overlay, '
    + '.palette-overlay, .project-browser-backdrop, '
    + '.timeline-editor-overlay, .sk-scrim, .canvas-ctx-backdrop, '
    + '.canvas-ctx-menu, .item-ctx-backdrop, .item-ctx-menu';
  function onGestureStart(ev: Event): void {
    if ((ev.target as HTMLElement | null)?.closest(PINCH_GUARD_SELECTOR)) {
      ev.preventDefault();
    }
  }
  onMount(() => {
    document.addEventListener('gesturestart', onGestureStart, { passive: false });
    document.addEventListener('gesturechange', onGestureStart, { passive: false });
  });
  onCleanup(() => {
    document.removeEventListener('gesturestart', onGestureStart);
    document.removeEventListener('gesturechange', onGestureStart);
  });

  // One-tap connect via `?bridge=<url>` (2026-08-13) - ConnectPage only
  // mounts when connectOpen() opens Navigate's Computers section, so
  // a query param present at boot needs something at the ALWAYS-mounted
  // App root to open Connect first. ConnectPage's own onMount does the
  // actual detection + pairing + stripping the param once it mounts —
  // this just makes sure it gets the chance to.
  onMount(() => {
    if (new URLSearchParams(window.location.search).has('bridge')) openConnect();
  });

  // Skin (DEC-130) is stamped on <html>, not only on .shell. The shell also
  // carries it so a screenshot of the shell alone is still skinned, but ROOT
  // is the one that matters: three surfaces are body-portalled by DEC-096
  // exemption (Matrix Viewer, Matrix Picker, Timeline editor) and would
  // otherwise render with the pre-skin token values.
  createEffect(() => {
    document.documentElement.dataset.skin = activeSkin();
  });
  onCleanup(() => window.removeEventListener('keydown', onCanvasMenuKey));
  // Splash gate (multi-bridge plan §"headline shape", point 3) is gone —
  // the PWA boots straight in regardless of bridge state. Per-control
  // greying lives on individual surfaces; no app-wide blocker.
  // Last transport snapshot from the M4L device. Null until the first packet
  // arrives. The Header uses this to show real `bar.beat.sixteenth` instead
  // of the phase strip when transport is live (<2s stale).
  const [transport, setTransport] = createSignal<{
    bar: number; beat: number; sixteenth: number;
    isPlaying: boolean; tempo: number; sigNum: number; sigDen: number;
    songTimeBeats: number;
    ts: number;
  } | null>(null);
  // Re-render Ableton-presence badge every second so "X ago" stays accurate
  // even when nothing's arriving.
  const [nowTick, setNowTick] = createSignal(Date.now());
  const presenceInterval = setInterval(() => setNowTick(Date.now()), 1000);
  onCleanup(() => clearInterval(presenceInterval));

  // ─── Live-transport slave state ───────────────────────────────
  // Threshold to call transport "fresh." Mirrors Header.tsx's TRANSPORT_STALE_MS
  // (kept duplicate-but-explicit so both files are readable standalone).
  const TRANSPORT_FRESH_MS = 2000;
  // True when a transport packet arrived in the last TRANSPORT_FRESH_MS, i.e.
  // Live's M4L OPButtons Transport device is actively talking to us. When true,
  // Live owns BPM/beat/playing; the StubClock tick handler stays out of the way.
  const isSlaved = createMemo(() => {
    const t = transport();
    if (!t) return false;
    return nowTick() - t.ts < TRANSPORT_FRESH_MS;
  });

  // Beats per bar for the active clock source — the live time-sig when
  // slaved, else the practice quantum. Drives the smoothClock quantum
  // (below) AND the LFO armed-countdown (SD-7, "in N beats to the next bar").
  const beatsPerBar = createMemo(() => {
    if (isSlaved()) {
      const t = transport();
      const num = t && t.sigNum > 0 ? t.sigNum : 4;
      const den = t && t.sigDen > 0 ? t.sigDen : 4;
      return num * (4 / den);
    }
    return practiceQuantum();
  });

  // Time signature + run-state of the ACTIVE clock, for the `timesig` and
  // `transport` Display sources. Same live-vs-practice rule as `beatsPerBar`
  // directly above, deliberately derived from the same `isSlaved()` gate
  // rather than re-deciding it - two places answering "which clock is real"
  // with slightly different logic is how a readout ends up disagreeing with
  // the Header it sits next to.
  const activeSig = createMemo<[number, number]>(() => {
    if (isSlaved()) {
      const t = transport();
      // 0 means "never received" upstream; fall back rather than render 0/0.
      return [t && t.sigNum > 0 ? t.sigNum : 4, t && t.sigDen > 0 ? t.sigDen : 4];
    }
    return [practiceSigNum(), practiceSigDen()];
  });
  const activeIsPlaying = createMemo(() => (isSlaved() ? (transport()?.isPlaying ?? false) : practiceIsPlaying()));

  // Smooth clock — re-anchors on every bridge tick, interpolates between
  // them at 60fps via requestAnimationFrame. The Header reads beat/bpm
  // from here, not directly from the bridge, so the counter moves
  // continuously instead of stepping every 33ms.
  const clock = createSmoothClock({ quantum: 4 });
  const bpm = clock.bpm;
  const phase = clock.phase;

  // Legacy bridge `transport`/`tick`/`welcome`/`midi.*` handlers and the
  // VITE_LEGACY_BRIDGE opt-in connect were REMOVED 2026-08-20 —
  // `packages/bridge/` (the daemon they talked to) is deleted, confirmed
  // unused for months. The OPBridge `/clock/status` poll (clockPoll.ts,
  // wired below) is the sole live clock source; macro/sequencer state comes
  // directly from `enginesStatePoll.ts` (see `lfo/runtime.ts` for the LFO
  // equivalent, closing the asymmetry this migration used to have).

  // Esc clears the inspector selection. Also exits ButtonGrid's
  // edit-cells mode when it's active, and cancels an armed Move (so the
  // user has one keystroke that means "get me out of whatever nested edit
  // surface I'm in"). Window-level so it works regardless of focus.
  //
  // The item context menu closes itself on Escape from its own capture-phase
  // listener and stops propagation there, so a menu-open Escape never also
  // clears the selection behind it — one Escape, one thing.
  function onKeyDown(ev: KeyboardEvent) {
    if (ev.key !== 'Escape') return;
    stopEditingCells();
    disarmItemMove();
    cancelPickButton();
    if (selectedItem()) selectItem(null);
  }
  window.addEventListener('keydown', onKeyDown);
  onCleanup(() => window.removeEventListener('keydown', onKeyDown));

  // The browser's back (2026-11-03, operator: "if i click back button, it should stay within opbuttons"): one press
  // steps back one thing (navigate/backButton.ts) and never leaves the app. On the canvas, the last steps: the page
  // menu, then what Esc lets go of (the Inspector's item, a move, cell editing).
  installBackGuard();
  // The browser's other ways out and in (2026-11-03): "Leave site?" mid-show, a stray file drop, page zoom, and what a
  // reload reopens. navigate/leaveGuard.ts, browserGuards.ts, resume.ts.
  installLeaveGuard();
  installBrowserGuards();
  onMount(() => installResume(!!activeProject()));
  onCleanup(onBack(() => {
    if (canvasMenu()) { closeCanvasMenu(); return true; }
    if (!selectedItem() && !moveArmedItemId()) return false;
    stopEditingCells();
    disarmItemMove();
    cancelPickButton();
    selectItem(null);
    return true;
  }, 'canvas'));

  // ARROW KEYS NUDGE the highlighted controls in edit mode (2026-10-10): 1px,
  // Shift for 10. A held key repeats; the whole burst is ONE undo step, closed
  // when the last arrow key comes up. Not while typing in a field or while
  // anything sits over the canvas (Navigate, a picker, a modal), where the
  // arrows belong to that surface.
  const ARROWS: Record<string, [number, number]> = {
    ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1],
  };
  const arrowsDown = new Set<string>();
  function typingInField(t: EventTarget | null): boolean {
    if (!(t instanceof HTMLElement)) return false;
    return t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName);
  }
  function endNudgeBurst(): void {
    if (arrowsDown.size === 0) return;
    arrowsDown.clear();
    projectHistory.endBatch();
  }
  function onNudgeKey(ev: KeyboardEvent) {
    const dir = ARROWS[ev.key];
    if (!dir || ev.ctrlKey || ev.metaKey || ev.altKey) return;
    if (!isEditMode() || anyModalOpen() || typingInField(ev.target)) return;
    if (selectedItemIds().length === 0) return;
    ev.preventDefault();
    if (arrowsDown.size === 0) projectHistory.beginBatch();
    arrowsDown.add(ev.key);
    const step = ev.shiftKey ? 10 : 1;
    nudgeSelection(dir[0] * step, dir[1] * step);
  }
  function onNudgeKeyUp(ev: KeyboardEvent) {
    if (!arrowsDown.has(ev.key)) return;
    arrowsDown.delete(ev.key);
    if (arrowsDown.size === 0) projectHistory.endBatch();
  }
  window.addEventListener('keydown', onNudgeKey);
  window.addEventListener('keyup', onNudgeKeyUp);
  // A key released while the window is not focused never sends its keyup;
  // close the burst anyway so undo is never left batching.
  window.addEventListener('blur', endNudgeBurst);
  onCleanup(() => {
    window.removeEventListener('keydown', onNudgeKey);
    window.removeEventListener('keyup', onNudgeKeyUp);
    window.removeEventListener('blur', endNudgeBurst);
    endNudgeBurst();
  });

  // A menu-armed Move now SURVIVES its drop (2026-08-09, operator: *"keep it
  // movable until user clicks off the item"*), so this is what ends it: the
  // first press that lands anywhere other than the armed item itself.
  //
  // Window-level and CAPTURE phase, both load-bearing. Window, because
  // "somewhere else" includes the header, a page pill, a modal and the empty
  // canvas, not just other items - one listener covers every surface that
  // exists and every one added later. Capture, because half the app stops
  // propagation on pointerdown (EditableItem's non-primary-button swallow,
  // every continuous control's drag handler, the context-menu backdrop), and
  // a bubble-phase listener would simply never hear those presses - the arm
  // would then persist with the item's trigger suppressed, which is the "dead
  // control" failure the old disarm-on-drop existed to prevent.
  //
  // Presses on the item's OWN context menu do disarm, and that is deliberate:
  // the menu is a different surface, and picking Move from it re-arms
  // immediately afterwards (the row's handler runs after this one).
  function disarmOnPressOutside(ev: PointerEvent) {
    const armed = moveArmedItemId();
    if (!armed) return;
    const el = (ev.target as HTMLElement | null)?.closest?.('.editable-item') as HTMLElement | null;
    if (el?.dataset.itemId === armed) return;
    disarmItemMove();
  }
  window.addEventListener('pointerdown', disarmOnPressOutside, true);
  onCleanup(() => window.removeEventListener('pointerdown', disarmOnPressOutside, true));

  // Pick-a-button mode (DisplayItem source=button "click on canvas",
  // 2026-08-12) is a commitment to "pick or bail", same shape as the
  // move-arm above: any press that does NOT land on a pickable Button item
  // cancels it. Capture phase + window-level for the same reason as
  // `disarmOnPressOutside` - it has to see the press before anything else
  // (a modal backdrop, another control's own drag handler) can swallow it.
  // A press that DOES land on a button is deliberately let through here so
  // EditableItem's own wrapper handler resolves the pick - this listener
  // only ever CANCELS, never resolves.
  //
  // ALSO deselects (closes the Inspector), not just cancels the pick.
  // `InspectorPanel`'s own narrow-viewport backdrop is suppressed for the
  // whole DisplayItem selection while a pick is in flight (it would
  // otherwise swallow the very canvas taps this feature exists to receive -
  // see InspectorPanel.tsx's backdrop `<Show>` guard) - so if this handler
  // only cancelled the pick, the Display would stay selected, the backdrop
  // would reactively remount on the SAME press this listener is reacting
  // to, and a genuine race opened up (confirmed empirically: a Playwright
  // click at that point retried forever against a backdrop that kept
  // reappearing). Closing outright removes the backdrop from the equation
  // entirely, and matches the backdrop's own normal "tap outside closes"
  // behaviour in every other Inspector context.
  function cancelPickOnPressOutside(ev: PointerEvent) {
    if (!pickButtonForDisplayId()) return;
    const el = (ev.target as HTMLElement | null)?.closest?.('.editable-item') as HTMLElement | null;
    if (el?.dataset.kind === 'button') return;
    cancelPickButton();
    selectItem(null);
  }
  window.addEventListener('pointerdown', cancelPickOnPressOutside, true);
  onCleanup(() => window.removeEventListener('pointerdown', cancelPickOnPressOutside, true));

  // ── MARQUEE SELECT (2026-08-09) ──────────────────────────────────────
  // Operator: *"add a ctrl click drag from empty space to marquee select
  // items"*, then *"keep the ctrl drag marquee in edit mode on the desktop
  // too"*, then *"if user does ctrl and drag, throw up a popup saying
  // 'marquee tool in edit page'"*.
  //
  // Two triggers, one behaviour, because the primary device has no ctrl key:
  //   * mouse  - ctrl (or cmd) held, drag from empty canvas;
  //   * touch  - a plain drag from empty canvas. Nothing else claims that
  //     gesture in edit mode (the canvas menu is a two-finger tap, DEC-089),
  //     so it costs no existing affordance.
  // Both EDIT MODE ONLY. A performance surface where dragging the background
  // accumulates a selection is not something to hand someone mid-set.
  //
  // ADDITIVE. The box unions into whatever is already highlighted, matching
  // ctrl's meaning everywhere else here; clicking bare canvas is the clear.
  const [marquee, setMarquee] = createSignal<
    { x0: number; y0: number; x1: number; y1: number } | null
  >(null);
  /** Set for one click after a marquee so the canvas's own click-to-deselect
   *  doesn't immediately undo the selection the drag just made. */
  let marqueeJustEnded = false;
  /** Rate-limits the "wrong mode" toast to one per gesture. */
  let marqueeHintShown = false;

  const MARQUEE_MIN_PX = 6; // smaller than this was a click, not a box

  /** What pressed the canvas last, so its click handler can tell a mouse
   *  click (does nothing on empty canvas) from a tap (deselects). */
  let lastCanvasPointerType = '';

  function onCanvasPointerDown(ev: PointerEvent) {
    lastCanvasPointerType = ev.pointerType;
    const onItem = !!(ev.target as HTMLElement | null)?.closest?.('.editable-item');
    if (onItem) return;                       // items own their own presses
    if (ev.pointerType === 'mouse' && ev.button !== 0) return;
    const modifier = ev.ctrlKey || ev.metaKey;
    const wants = ev.pointerType === 'mouse' ? modifier : isEditMode();
    if (!wants) return;
    if (!isEditMode()) {
      // The discoverability half of the ask: say where the tool lives rather
      // than doing nothing and looking broken. Once per gesture.
      if (!marqueeHintShown) {
        marqueeHintShown = true;
        pushToast('warn', 'Marquee tool is in Edit Page');
        setTimeout(() => { marqueeHintShown = false; }, 1200);
      }
      return;
    }
    const start = viewportToCanvas(ev.clientX, ev.clientY);
    if (!start) return;
    ev.preventDefault();
    const before = selectedItemIds();
    setMarquee({ x0: start.x, y0: start.y, x1: start.x, y1: start.y });

    const onMove = (e: PointerEvent) => {
      const p = viewportToCanvas(e.clientX, e.clientY);
      const m = marquee();
      if (!p || !m) return;
      setMarquee({ ...m, x1: p.x, y1: p.y });
      setSelectedItems([...before, ...idsInsideMarquee()]);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      const m = marquee();
      const dragged = !!m
        && (Math.abs(m.x1 - m.x0) > MARQUEE_MIN_PX || Math.abs(m.y1 - m.y0) > MARQUEE_MIN_PX);
      if (dragged) marqueeJustEnded = true;
      else setSelectedItems(before);   // a stray click inside the gesture
      setMarquee(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }

  /** Items whose box INTERSECTS the marquee. Intersection, not containment:
   *  requiring a full enclose means a lasso around a dense page has to be
   *  drawn wider than the page, which on a 1180px frame is often impossible. */
  function idsInsideMarquee(): string[] {
    const m = marquee();
    if (!m) return [];
    const left = Math.min(m.x0, m.x1);
    const right = Math.max(m.x0, m.x1);
    const top = Math.min(m.y0, m.y1);
    const bottom = Math.max(m.y0, m.y1);
    return layoutItems()
      .filter((it) => it.x < right && it.x + it.w > left && it.y < bottom && it.y + it.h > top)
      .map((it) => it.id);
  }

  // Shell scale-to-fit. Recomputes whenever the viewport resizes OR the
  // active project's canvas size changes (e.g. user switches between an
  // iPad-landscape project and an iPhone-portrait one).
  let viewportRO: ResizeObserver | undefined;
  const frameReserve = createMemo(() => {
    const p = activeProject();
    return p ? reserveOf(p) : { top: 0, bottom: 0 };
  });
  function recalcShellScale() {
    if (!viewportFitRef) return;
    const size = activeCanvasSize();
    const boxW = viewportFitRef.clientWidth;
    const boxH = viewportFitRef.clientHeight;
    // Navigate docks beside the canvas (DEC-268) on a desktop window with
    // room for its sidebar AND a canvas still readable beside it; a window
    // that would have to shrink the stage past the readable floor keeps the
    // full-screen launcher instead of docking.
    setDockCapable(measureDockCapable(boxW, boxH)
      && (!size || fitScale(size, { w: boxW - DOCK_SIDE_W, h: boxH - DOCK_HEAD_H }, Infinity) >= READABLE_SCALE_FLOOR));
    const inL = dockChromeShown() ? DOCK_SIDE_W : 0;
    const inT = dockChromeShown() ? DOCK_HEAD_H : 0;
    document.documentElement.style.setProperty('--nav-dock-top', `${inT}px`);
    document.documentElement.style.setProperty('--nav-dock-left', `${inL}px`);
    if (!size) {
      if (boxW > 0 && boxH > 0) setBareFrame({ w: Math.min(boxW, 1180), h: Math.min(boxH, 820) });
      setShellScale(1); setShellShift({ x: 0, y: 0 }); setInspectorDocked(false); recalcInspectorSafeInsets(); return;
    }
    // The space the canvas is fitted into: the window, less Navigate's
    // sidebar and header while they are docked on screen.
    const availW = boxW - inL;
    const availH = boxH - inT;
    if (availW <= 0 || availH <= 0) return;
    // ONE implementation of the formula, shared with the new-project form's
    // fit preview (devices.ts). Inlining it here again is how the form ends
    // up promising a size the app does not actually deliver.
    // The Inspector's dock (layout/inspectorDock.ts): with a real pointer
    // and room to spare, the canvas gives the open Inspector a column of its
    // own instead of sitting under it. The iPad never gets here with room.
    const finePointer = typeof matchMedia === 'function' && matchMedia('(hover: hover) and (pointer: fine)').matches;
    // Docked, the frame fills the space it has, past 100% ("use full window"),
    // unless the canvas bar's size button is on 100%.
    const cap = navigateDocked() && !canvasActualSize() ? Infinity : 1;
    const fit = dockDecision(size, { w: availW, h: availH }, inspectorWanted() && finePointer, READABLE_SCALE_FLOOR, cap);
    if (fit.dock !== untrack(inspectorDocked)) {
      setDockAnim(true);
      if (dockAnimTimer !== undefined) clearTimeout(dockAnimTimer);
      // When the slide has finished, measure the header again: the read
      // below happens at the START of the transition, and the narrow panel's
      // header clearance (--insp-safe-top) must be where it ended.
      dockAnimTimer = window.setTimeout(() => { setDockAnim(false); recalcInspectorSafeInsets(); }, 320);
      setInspectorDocked(fit.dock);
    }
    setShellScale(fit.scale);
    // The shell is centred in the whole window; these move it to the centre
    // of the space it is fitted into (right of Navigate, below its header,
    // left of a docked Inspector).
    const reserve = fit.dock ? DOCK_RESERVE_W : 0;
    setShellShift({ x: inL / 2 - reserve / 2, y: inT / 2 });
    recalcInspectorSafeInsets();

    /* AUTO-FIT (2026-09-22). If this project is drawn for a different-shaped
       screen than the one it is on, and nothing would be clipped, adopt the
       right frame rather than letterboxing and waiting to be asked. Guards,
       and the five rounds of the operator reporting the dead bands that led
       here, are in layout/autoFit.ts. Runs off the MEASURED fit box for the
       same reason the scale does. Safe to call on every recalc: it is a
       no-op once it has fired for a project. */
    // The strips this device gives up to iOS, recorded into the project so
    // the desktop previews the same canvas (autoFit.ts maybeRecordReserve).
    maybeRecordReserve();
    const fitted = maybeAutoFit(availW, availH);
    if (fitted) {
      pushToast('ok',
        `Fitted to this screen: ${fitted.to.w}x${fitted.to.h}`
        + ` (was ${fitted.from.w}x${fitted.from.h}).`,
        6000);
    }
  }
  /* Pin the fit box to the real window, in pixels.
     `100vw`/`100dvh` are a first-paint fallback only - see the note on
     `.viewport-fit`.

     ⚠️ THE APIS DO NOT DISAGREE - THEY ARE ALL WRONG TOGETHER, and reading
     this comment's previous four versions as "pick the honest one" is what
     cost ten fix rounds. On an installed iOS web app with `viewport-fit=cover`
     plus `black-translucent`, WebKit paints from y=0 but computes the window
     height as if it had been inset below the status bar (WebKit #254868).
     `innerHeight`, `documentElement.clientHeight`, `visualViewport.height`,
     `100dvh` and `100svh` all returned 778 on the operator's 810pt iPad. The
     32pt shortfall lands at the BOTTOM, outside the paintable surface, so no
     measurement and no CSS anywhere in the document could ever have filled it.

     `100vh` is the one unit the inset does not corrupt. `measureViewportBox`
     owns that, and the reasoning, and the reason it is only trusted in an
     installed app. NOT `visualViewport` under any circumstances: that is the
     VISUAL viewport, which shrinks for the on-screen keyboard and pinch-zoom,
     and using it would collapse the whole rig the moment a field took focus. */
  function syncViewportBox() {
    if (!viewportFitRef) return;
    const box = measureViewportBox();
    if (!box) return;
    viewportFitRef.style.width = `${box.w}px`;
    viewportFitRef.style.height = `${box.h}px`;
  }
  onMount(() => {
    syncViewportBox();
    recalcShellScale();
    if (viewportFitRef) {
      viewportRO = new ResizeObserver(recalcShellScale);
      viewportRO.observe(viewportFitRef);
    }
    const onViewportChange = () => { syncViewportBox(); recalcShellScale(); };
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('orientationchange', onViewportChange);

    /* SCROLL LOCK, installed-app only (2026-09-22).
       `app.css` takes `overflow: hidden` off the root in standalone, because
       on the root it propagates to the VIEWPORT and clips the bottom 32pt of
       the screen away (WebKit #254868 - the whole bottom-band arc). The clip
       moved to the app container, which bounds the real box instead.

       The side effect is that the document is now 32pt taller than the layout
       viewport, so the page can be scrolled by exactly that much - and a stray
       vertical swipe shunting the rig 32pt mid-set is precisely what SD-3's
       surface hardening exists to prevent. Pinning it back costs one listener.

       `scrollTo` rather than re-adding an overflow rule anywhere: any clip that
       resolves against the short viewport puts the band straight back. */
    const lockScroll = () => {
      if (window.scrollY !== 0 || window.scrollX !== 0) window.scrollTo(0, 0);
    };
    if (isStandalone()) window.addEventListener('scroll', lockScroll, { passive: true });
    onCleanup(() => {
      window.removeEventListener('resize', onViewportChange);
      window.removeEventListener('orientationchange', onViewportChange);
      window.removeEventListener('scroll', lockScroll);
    });
  });
  createEffect(() => {
    // React to project canvas size changes (signal dep tracked).
    activeCanvasSize();
    recalcShellScale();
  });
  // ...and to an item being inspected or let go, which docks or undocks it.
  createEffect(on(inspectorWanted, () => recalcShellScale(), { defer: true }));
  // ...and to Navigate's docked chrome showing or hiding, or the size button
  // (DEC-268): the frame slides and scales into the new space, animated the
  // same way the Inspector's dock is.
  createEffect(on(() => [dockChromeShown(), canvasActualSize(), navigateDocked()] as const, () => {
    setDockAnim(true);
    if (dockAnimTimer !== undefined) clearTimeout(dockAnimTimer);
    dockAnimTimer = window.setTimeout(() => { setDockAnim(false); recalcInspectorSafeInsets(); }, 320);
    recalcShellScale();
  }, { defer: true }));
  onCleanup(() => viewportRO?.disconnect());

  // InspectorPanel safe-insets (fixes the CP-083 header-overlap bug).
  //
  // The InspectorPanel is deliberately a document-level sibling of `.shell`
  // (see the comment above `anyModalOpen`) so it renders at true viewport
  // pixels, never shrinking to iPad-preview scale - necessary for it to stay
  // readable on a desktop dev window. That same independence is what caused
  // the bug: its narrow/dock CSS variant anchored `top: var(--space-3)` from
  // the raw browser viewport edge, which only happens to clear the header
  // when the shell is unscaled AND unscrolled AND top-anchored. A first fix
  // attempt (a fixed `--chrome-bar-h` push-down) was proven insufficient by
  // re-probing at a deliberately mismatched window size and reverted rather
  // than shipped half-verified - a static offset cannot know how much the
  // shell is currently scaled/letterboxed, or whether the chrome bar is
  // pinned to the shell's top or bottom edge (`barPosition()`, a Settings
  // toggle).
  //
  // Fixed properly here: measure the LIVE `.hdr` element's own rendered
  // rect (whatever scale/letterbox/bar-position produced it - this is not
  // reproducing the scale math, it is reading where the browser actually
  // painted the header) and publish two CSS custom properties in real
  // viewport px, `--insp-safe-top` / `--insp-safe-bottom`, that the narrow
  // InspectorPanel variant clears in app.css. rAF-deferred so the read
  // happens after the triggering `transform`/style write has actually been
  // committed and laid out by the browser, regardless of Solid's internal
  // effect-ordering between this and the shell's own transform binding.
  let insetsRaf: number | null = null;
  function recalcInspectorSafeInsets() {
    if (insetsRaf !== null) return;
    insetsRaf = requestAnimationFrame(() => {
      insetsRaf = null;
      const root = document.documentElement;
      const hdr = shellElRef?.querySelector('.hdr');
      if (!hdr) {
        root.style.setProperty('--insp-safe-top', '0px');
        root.style.setProperty('--insp-safe-bottom', '0px');
        return;
      }
      const r = hdr.getBoundingClientRect();
      if (barPosition() === 'bottom') {
        root.style.setProperty('--insp-safe-top', '0px');
        root.style.setProperty('--insp-safe-bottom', `${Math.max(0, window.innerHeight - r.top)}px`);
      } else {
        root.style.setProperty('--insp-safe-top', `${Math.max(0, r.bottom)}px`);
        root.style.setProperty('--insp-safe-bottom', '0px');
      }
    });
  }
  onCleanup(() => { if (insetsRaf !== null) cancelAnimationFrame(insetsRaf); });
  // barPosition toggling doesn't go through recalcShellScale (no resize, no
  // canvas-size change) but does move the header to the opposite edge, so it
  // needs its own trigger.
  createEffect(() => {
    barPosition();
    recalcInspectorSafeInsets();
  });

  // The first-run gate is decided at mount (`projects().length === 0`), but
  // projectSync can restore projects a second or two LATER, once the primary
  // bridge connects. Without this the operator watches their rig reappear
  // behind a non-dismissible "you must create a project" modal. Fires once,
  // and only for a gate that was actually forced - it must never close a
  // browser the operator opened deliberately.
  let gateWasForced = projects().length === 0;
  createEffect(() => {
    if (!gateWasForced) return;
    if (activeProject()) {
      gateWasForced = false;
      setBrowserOpen(false);
    }
  });

  /** True when the active project's canvas cannot render readably here. */
  const fitMismatch = createMemo(
    () => !!activeProject() && shellScale() < READABLE_SCALE_FLOOR,
  );

  // Re-arm the notice when the mismatch clears OR the project changes.
  // Without the project-change leg, dismissing "this iPad-portrait project is
  // 64%" would also silently suppress the warning for a DIFFERENT bad project
  // opened five minutes later - which is the same lockout wearing a hat.
  let lastFitProjectId: string | null = null;
  createEffect(() => {
    const id = activeProject()?.id ?? null;
    const mismatch = fitMismatch();
    if (id !== lastFitProjectId) {
      lastFitProjectId = id;
      setFitNoticeDismissed(false);
      return;
    }
    if (!mismatch) setFitNoticeDismissed(false);
  });

  // Engines-state poller — translates OPBridge's
  // `GET /engines/opbuttons/state` snapshots into legacy `lfo.state`
  // event-bus messages so SineLfo sees real engine transitions
  // (armed → running → stopping → idle). Sprint Session 3 slice —
  // full WS `engine.state` subscription lands later.
  startEnginesStatePoll();

  // Timeline state poller — GET /timelines/opbuttons/state read-back
  // (2026-08-21) so TimelineLauncher's local visual sim can reconcile
  // against a Song paused/resumed/stopped by something other than this tab.
  // See timeline/statePoll.ts.
  startTimelineStatePoll();

  // Toggle-state poller — keeps FireButton's OPBridge-native toggle mode
  // (2026-08-16) showing the real lit-state on load/reconnect, including a
  // flip that happened via a pedal while this tab was closed.
  startToggleStatePoll();

  // OBS scene poller - lights an OBS Scene button from the LIVE program
  // scene rather than from a local latch, and catches a scene changed in OBS
  // itself or from a Stream Deck. Deliberately slow and demand-gated: each
  // read is a fresh obs-websocket handshake on the streaming PC, so it asks
  // only for bridges an OBS control on the current page actually points at,
  // only while the page is visible. See obs/scenePoll.ts's header.
  startObsScenePoll();

  // Run Program poller - lights a Run Program button while its program is
  // running on that machine, and turns it off when the program is quit or
  // crashes there. Cheap on OPBridge's side (config + an in-memory set), so
  // it runs at 3s, but still only for machines a program button on the
  // current page points at. See program/programPoll.ts.
  startProgramPoll();

  // Ableton device poller - lights an Ableton Device button from what
  // OP-ButtonsDevice reports, never from a local latch, so a device switched
  // by hand in Live still reads correctly. Two halves per tick (ask, then
  // read) and demand-gated harder than the OBS one: the ASK walks the LOM
  // device tree inside Live's own process during a gig. See
  // ableton/devicePoll.ts's header.
  startAbletonDevicePoll();

  // Clock poller — OPBridge `GET /clock/status` is the SOLE live clock
  // source (NEXT #1/#2 migration; the legacy WS `transport` handler was
  // removed 2026-07-18). Feeds the same transport signal + smoothClock
  // anchor that handler used to, so the existing
  // Header surface (transport readout, isSlaved, BPM "live" cue) works
  // off real OPBridge data. Fresh snapshots anchor + set transport
  // WHETHER PLAYING OR PAUSED (corrected 2026-08-22 — "fresh" used to also
  // require playing, so a real Ableton pause looked identical to "no
  // transport at all" once the last playing snapshot went stale, and the
  // readout reset to 0.0.0 instead of holding OPBridge's actual paused
  // position — see clockPoll.ts's translate() for the full chain). Only
  // genuinely stale snapshots (OPBridge unreachable / Ableton never
  // connected) fall back to the manual phase strip via transportLive()'s
  // stale-check. 4 Hz playing / 1 Hz idle.
  // The single clock sink: applies a fresh /clock/status snapshot to the
  // transport signal + smoothClock anchor. Named (not inline) so the DEV test
  // hook below can drive the EXACT production path — after the legacy
  // `bridge 'transport'` handler was removed (2026-07-18), this is the only
  // route into the clock, so tests must exercise it, not a stand-in.
  function applyClockSnapshot(snap: ClockSnapshot): void {
    watchJob('clock-apply');
    // The poller sets its own clockSource() signal (read by the Connect
    // status card + header pill); the sink only anchors off fresh snapshots.
    if (!snap.fresh) return;
    setTransport({
      bar: snap.bar,
      beat: snap.beat,
      sixteenth: snap.sixteenth,
      isPlaying: snap.isPlaying,
      tempo: snap.tempo,
      sigNum: snap.sigNum,
      sigDen: snap.sigDen,
      songTimeBeats: snap.songTimeBeats,
      ts: snap.ts,
    });
    // Freeze BEFORE anchoring, not after (corrected 2026-08-22 alongside
    // the fresh-requires-playing fix above). smoothClock.anchor() checks
    // its OWN frozen flag to decide whether to slew a small correction or
    // snap it immediately - anchoring first (old order) meant a
    // playing->paused transition still ran the slew path (frozen was
    // still false at that moment), landing on a small in-flight
    // correction that freeze() then discarded, freezing on an overshot
    // value instead of the true paused position. Freezing first means
    // anchor() sees frozen=true and hard-snaps to the exact reported
    // position on the very first paused snapshot, not one poll cycle
    // later. Beat ring, phase strip, and beat-aligned visuals all track
    // Live's real bar boundaries either way. When snapshots go stale
    // (>2s), transportLive() flips false and the manual phase strip
    // resumes (no anchor overwrites).
    clock.freeze(!snap.isPlaying);
    clock.anchor({ beat: snap.songTimeBeats, bpm: snap.tempo });
    setIsPlaying(snap.isPlaying);
  }
  startClockPoll(applyClockSnapshot);
  onCleanup(() => stopClockPoll());

  // Inbound MIDI, the second transport. OPBridge's `midi_in` push arm on
  // /ws/consumer is built (S242) and parsed since 2026-10-16; this poller
  // feeds the SAME `onMidiIn()` sink, each message delivered once whichever
  // brings it. It keeps running beside the push: on the Mac the push carried
  // the Pacer but not the Twister. It self-idles unless something is actually
  // subscribed - see midiInPoll.ts on why draining that route is delicate.
  startMidiInPoll();
  onCleanup(() => stopMidiInPoll());
  // Feeds `midi/inbound.ts`, StatusLed's sole data source. Self-gating: it
  // only subscribes while the project actually contains a consumer, which is
  // also what keeps the poll above from draining a destructive route for a
  // project that has no StatusLed in it. Owns its own effect + cleanup.
  startInboundMidiFeed();

  // Mixer feedback poller  -  DEC-467. Reads OPBridge GET /mixer/status (Live's
  // per-track volume/mute/name/meter from the OP-AbletonMixer feed) and drives
  // the mixer strips' feedback half, so a fader moved in Ableton updates the
  // iPad. Primary-only (DEC-063), silent on 401/503/network; echo-suppresses a
  // strip mid-drag. The control half (iPad -> Ableton) is unchanged (/osc/send).
  startMixerStatusPoll();
  onCleanup(() => stopMixerStatusPoll());

  // Stream Deck warm-up. Fetches the action catalogue as soon as a bridge is
  // connected, but ONLY when the project actually contains a Stream Deck
  // item. Two jobs: it recovers the Inspector from the ~3s connect race
  // (open it too early and the picker latches "No OPBridge connected"), and
  // it pays OPBridge's 5.9s lazy MCP-server cold start at boot instead of on
  // the first press of the set. See installStreamDeckPrewarm for why warming
  // on Inspector-open — OPBridge's suggestion — would miss the gig path.
  installStreamDeckPrewarm(
    () => anyConnectedReactive(),
    () => (activeProject()?.pages ?? []).some(
      (pg) => pg.items.some((i) => i.kind === 'streamdeck'),
    ),
  );

  // Consumer WebSocket  -  Phase B pairing silent-push (opbridge/consumerWs.ts).
  // Follows the primary's /ws/consumer: receives `inheritance_available`
  // (peer-bridge bearers, so a later change-primary needs no re-pair), resyncs
  // anything minted while the socket was down via GET /pairing/inheritances on
  // reconnect, and detects revocation out-of-band (that GET 401s  -  OPBridge
  // ships NO bearer_revoked push). Independent of the clock (that stays on the
  // /clock/status poll); the clock_marker frames riding this socket are ignored.
  startConsumerWs();
  onCleanup(() => stopConsumerWs());

  // The timeline lights the canvas (DEC-269). A timeline plays on its home
  // bridge, often not the primary, so every other paired bridge gets a
  // pwa_action-only socket; canvasLights turns `timeline_light` frames from
  // any of them into lit items, and countIn draws the ring before each one.
  startPeerPwaSockets();
  onCleanup(() => stopPeerPwaSockets());
  startCanvasLights();
  // OP-Motion capture answers (2026-10-24): a rule turns each `/OP-Motion/state` into a pwa_action, stored as a
  // landing preset by `timeline/motionCapture.ts`.
  startMotionCaptureListener();
  startCountIn({ beat: clock.beat, bpm: clock.bpm, beatsPerBar });
  installTimelineAutoPush();

  // Black box recorder. Writes a heartbeat + connection state to localStorage
  // so a force-quit does not destroy the record of whatever preceded it.
  // Started here, beside the connection machinery it watches, but deliberately
  // depending on NOTHING from it - state is read through this closure, so the
  // recorder cannot participate in an import cycle in the code under
  // observation. See `diag/blackBox.ts` for why this exists and what its
  // heartbeat column proves.
  // Watchdog (2026-10-08): a worker thread that tells a frozen page from a
  // stopped app and names the last job started. See `diag/watchdog.ts`.
  startWatchdog();
  startBlackBox(() => {
    const all = bridges();
    let ok = 0;
    for (const b of all) {
      const c = getConnection(b.id);
      if (c && c.status() === 'connected') ok += 1;
    }
    return { conn: ok > 0, ws: consumerWsConnected(), total: all.length, ok };
  });
  onCleanup(() => stopBlackBox());

  // Problem reports to OverpoweredApps (2026-10-05): the outbox retries what
  // could not be sent, and the badge shows replies in My reports. Both stay
  // quiet while the suite is performing.
  installReportOutbox();
  installHelpDeskBadge();

  // Inheritance drainer  -  DEC-465 (pairing auto-mint, OPButtons half). Turns the
  // inherited peer bearers that consumerWs surfaces into real bridge rows: adopts
  // a fresh bearer onto a reinstalled peer, or resolves a brand-new mesh bridge's
  // URL (via the primary's /peers/paired-bridges, joining on node_id) and adds it.
  // The end result is "pair once, never again" holds when a new/reinstalled bridge
  // joins  -  the credential lands with no re-pair prompt. Never churns the active
  // primary's bearer (see module doc).
  // REFRESH-ONLY, re-enabled 2026-09-07. It was switched off entirely on
  // 2026-08-30 because mesh auto-pairing "pushed one step too far" - but that
  // objection was to the module ADDING bridges nobody asked for, and switching
  // the whole thing off also removed the only path by which a stale per-bridge
  // bearer can ever recover.
  //
  // What that cost, measured on the operator's rig 2026-09-07: pair on bridge
  // A, then pair again on bridge B, and A's bearer is superseded server-side
  // while this device keeps using it - 403 on every call, once a second,
  // forever, with the PWA showing "connected" and "access revoked" at once.
  // Nothing here could adopt the replacement OPBridge had already minted.
  //
  // `autoAdd` stays false, so the rejected half stays rejected: no bridge the
  // operator did not pair will appear. Only credentials on rows they already
  // chose get refreshed, and never the address.
  startInheritanceDrainer({ autoAdd: false });
  onCleanup(() => stopInheritanceDrainer());

  // Persistent storage (2026-08-31). Safari evicts script-writable storage
  // after ~7 days unused, which for a fortnightly gigger means opening the app
  // at a venue to a blank shell - no layouts, no pairings. A granted origin is
  // exempt. Measured on the operator's iPad (26.6.1): persisted() false ->
  // persist() -> persisted() TRUE, so WebKit grants this to an installed app.
  // Fire-and-forget: the app works either way, it is just evictable without it,
  // and pre-flight reports the outcome rather than letting it fail silently.
  void requestPersistentStorage();

  // Bearer-revoked prompt. `consumerRevoked()` is re-derived from the server
  // each session (never persisted); show a one-shot "re-pair" choice on the
  // false->true edge, and let the user dismiss it (the badge stays in its alert
  // state regardless). Manual prev-tracking avoids a solid `on` import.
  const [revokePromptOpen, setRevokePromptOpen] = createSignal(false);
  let wasRevoked = false;
  createEffect(() => {
    const r = consumerRevoked();
    // When Connect is already open it shows its own "Access revoked" banner, so
    // suppress the modal prompt to avoid a duplicate surface (and close a stale
    // one if the user opens Connect afterward).
    const connectShowing = connectOpen();
    if (r && !wasRevoked && !connectShowing) setRevokePromptOpen(true);
    if (!r || connectShowing) setRevokePromptOpen(false);
    wasRevoked = r;
  });

  // Refresh the inheritance registry whenever the Connect modal opens, so the
  // change-primary candidate list is current even if the socket silently died.
  createEffect(() => { if (connectOpen()) pokeConsumerWs(); });

  // DEV/test hook: inject a clock snapshot straight into the production sink
  // above, no live poll required. Replaces the old `__opbuttons.simulateTransport`
  // (which drove the now-removed legacy `bridge 'transport'` handler). Also
  // exposes the REAL `clockSourceLabel` so the clock smoke asserts the actual
  // production mapping (incl. ableton_sync) instead of a re-derived table.
  // `header_bpm_reflects_transport` uses `injectSnapshot` to exercise the
  // Header's slaved/live-units display deterministically. DEV-only — never
  // attached in a production build.
  if (import.meta.env.DEV) {
    (window as unknown as { __opbuttonsClock?: {
      injectSnapshot(s: ClockSnapshot): void;
      sourceLabel(s: string): string;
    } }).__opbuttonsClock = {
      injectSnapshot: applyClockSnapshot,
      sourceLabel: clockSourceLabel,
    };
    // Show-mode surfaces for smokes: current wake-lock mode (SD-3) + the pure
    // layout linter (SD-14, run against a passed project so the dup-CC /
    // out-of-bounds detection is deterministically testable without UI).
    (window as unknown as {
      __opbuttonsShow?: { wakeLockMode(): string; lint(project: unknown): unknown };
    }).__opbuttonsShow = {
      wakeLockMode: () => wakeLockMode(),
      lint: (project: unknown) => lintProject(project as never, { matrixLabel: matrixCcLabel }),
    };
  }

  // Dev-time clock drift log (OPBridge ask "measurement discipline"). Opt in
  // with `?clockdebug=1` — smoothClock then logs each anchor's raw
  // extrapolated-vs-truth correction, so slew quality is measured, not
  // eyeballed. Off by default; zero cost in normal use.
  if (typeof location !== 'undefined'
      && new URLSearchParams(location.search).get('clockdebug') === '1') {
    setSmoothClockDebug(true);
  }

  // Resync-on-resume (OPBridge clock-design ask, Build 2c). iPad Safari
  // throttles rAF + timers when the PWA backgrounds / the screen dims, so
  // the playhead freezes at a stale position. On foreground, force an
  // immediate clock poll so a fresh anchor snaps it back to truth instead of
  // waiting out the (up to 1s) idle cadence. `pageshow` covers bfcache
  // restores that don't fire `visibilitychange`.
  function onResume() {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    pokeClockPoll();
    pokeConsumerWs();
    pokeMixerStatusPoll();
  }
  document.addEventListener('visibilitychange', onResume);
  window.addEventListener('pageshow', onResume);
  onCleanup(() => {
    document.removeEventListener('visibilitychange', onResume);
    window.removeEventListener('pageshow', onResume);
  });

  // ─── Practice clock (OPBridge clock-design ask, Build 1) ──────
  // A local operator transport drives smoothClock whenever no LIVE clock is
  // fresh (`!isSlaved()`): disconnected, or connected-but-Ableton-stopped.
  // The live clock path (the OPBridge poll sink `applyClockSnapshot` above) is
  // the SOLE owner of the clock while slaved; these effects stand down then and
  // resume when it goes stale. A jump at the handoff is expected + acceptable
  // (the operator started the real transport). This supersedes the legacy
  // Node-bridge stub clock — its tick/transport clock role was removed
  // 2026-07-18 and `VITE_LEGACY_BRIDGE` is deprecated.

  // Bar length follows whichever source is active, so the phase strip / beat
  // ring wrap on the right boundary (live time-sig when slaved, else the
  // practice time-sig).
  createEffect(() => {
    clock.setQuantum(beatsPerBar());
  });

  // Play/stop + tempo. Re-anchors at the CURRENT beat with the practice
  // tempo — a BPM change glides without a phase jump; freeze mirrors
  // play/stop. The clock mutations run inside `untrack` because
  // `clock.anchor` reads the `beat` signal internally (for its slew
  // correction); without untrack that read would make this effect depend on
  // `beat` and re-fire every animation frame, re-anchoring and freezing the
  // clock. Only practiceIsPlaying / practiceBpm / isSlaved are real deps.
  createEffect(() => {
    const playing = practiceIsPlaying();
    const b = practiceBpm();
    if (isSlaved()) return; // live path owns the clock
    untrack(() => {
      // While playing, resume from the current beat (a BPM change glides).
      // On STOP, reset to beat 1 (bar start) so the next play begins from the
      // downbeat rather than wherever the operator happened to stop.
      clock.anchor({ beat: playing ? clock.beat() : 0, bpm: b });
      clock.freeze(!playing);
      setIsPlaying(playing);
    });
  });

  // Set-beat-1: re-anchor phase to 0 at this instant. A separate effect so a
  // downbeat tap is unambiguous (the play/stop effect can't tell why it ran).
  // Only practiceDownbeatEpoch is a real dep; everything else (incl. the
  // `beat` read inside anchor) is untracked to avoid a per-frame re-fire.
  createEffect(() => {
    practiceDownbeatEpoch(); // dep: fires on each tap
    untrack(() => {
      if (isSlaved() || !practiceIsPlaying()) return;
      clock.anchor({ beat: 0, bpm: practiceBpm() });
      clock.freeze(false);
    });
  });

  // Ctrl+Z / Ctrl+Y / Ctrl+Shift+Z routed to whichever store was
  // most-recently-edited (see undo/history.ts). Falls through when
  // focus is inside an <input>/<textarea>/contenteditable so browser
  // native input undo still works there.
  const uninstallUndo = installGlobalUndoRedoHandler();
  onCleanup(() => uninstallUndo());

  // Whole-rig snapshot sync to the legacy bridge (disaster-recovery
  // backup/restore) REMOVED 2026-08-20 — `bundle/syncToBridge.ts` deleted,
  // `packages/bridge/` (what it pushed to) is deleted, and this had zero
  // live path for months regardless (nothing ever set `bridgeConnected`
  // true). No OPBridge-side equivalent exists yet — flagged as a real
  // capability gap, not silently dropped; see NEXT.md.
  // (The Waves-autotune doc publish, `autotune/docSync.ts`, was REMOVED
  // 2026-09-28 at the operator's call: OPBridge's DEC-562 reads the autotune
  // port from its own rig config for both capabilities and the send, so
  // nothing read the docs any more. DEC-102 RETIRED.)
  // Re-bind an auto-detectable pedal whose stored bridge id no longer
  // resolves. Without this the Helix greys out permanently after the row it
  // was pinned to goes away, and only a manual RE-DETECT brings it back.
  // BEFORE the rebind sweep, deliberately: migrating a pedal's binding to a
  // stable node id can make it resolve again, and a pedal that resolves is
  // not stale. Running rebind first would re-detect it and write a fresh
  // device-local id on the way past.
  // Per-ITEM bridge affinity carries the same device-local-id defect the
  // pedal migration below fixes, on 18 item types. Same rule, same reason.
  // Setlist state - primes GET /setlist/state on connect and on reconnect.
  // Live updates ride the `setlist_update` push (consumerWs), so there is
  // deliberately no interval: a poll costs most exactly when it matters
  // least to be costing anything, which is the operator's own constraint.
  installSetlistPrime();
  installBridgeAffinityMigration();
  installPedalNodeMigration();
  installPedalRebind();
  // Mirror the projects document to the OPBridge doc store, which writes it
  // to disk under the OPBridge data root. localStorage stays the source of
  // truth; this is the copy that survives a browser data clear or a dead
  // device. Pull is automatic and unconditional (DEC-146 reversal) — see
  // projectSync.ts.
  installProjectSync();
  // Every timeline belongs to a project and travels in its sync doc (plan
  // 3.1); this gives the ones that have no owner yet (a device's existing
  // library, or a deleted project's) one. timeline/librarySync.ts.
  installTimelineOwnership();
  // Guitar drop tuning moved onto the project (synced) 2026-09-28; carry this
  // device's old per-device value into a project that has none.
  installDropTuningCarryOver();
  // Mirror the Matrix (CC/note/PC device registry) the same way — push on
  // every edit, pull once per fresh primary connection (no poll; matrix
  // edits are rare). See matrixSync.ts for why this rides the doc store
  // instead of OPBridge's mismatched /matrix REST entity CRUD.
  installMatrixSync();
  // Mirror the Rig State library (captured banks/looks) the same way —
  // OPBridge ask S226 (2026-08-20): unlike Macros/LFO/Sequencer, a captured
  // look has no other source than this browser, so a data-clear destroys it
  // outright. Push on every edit, pull once per fresh primary connection.
  // See rigStateSync.ts.
  installRigStateSync();
  // Read the lighting rig from OPBridge, which owns it, on every bridge
  // connect (not polled - a rig changes when it is repatched, not
  // continuously). Degrades to the local fixture library and says why.
  // See fixtures/bridgeSync.ts.
  installFixtureSync();
  // Read the fixture make/model CATALOGUE from OPBridge too, same cadence.
  // Separate from the rig above because they are separate facts: the rig is
  // user-scoped and peer-synced, the catalogue is build-time const data on
  // the daemon. Order between the two does not matter - bridgeFixtures()
  // derives its translation, so whichever lands second re-maps the first.
  // See fixtures/profileSync.ts for what was measured wrong without it.
  installProfileSync();
  // Phase 4: poll what the lights are ACTUALLY doing. Unlike the two reads
  // above this is live performance state, so there is no connect event to
  // hang it on - but it is gated three ways (a lighting control must be
  // mounted, a bridge must be connected, the document must be visible) so an
  // iPad on a page of MIDI buttons, or asleep in a bag, never sends a request.
  installLightingLiveState();
  // DEC-154 inbound MIDI trigger, A-path Sequencer-arm slice: push the
  // whole rule set to POST /rules/opbuttons on every binding edit, and keep
  // each bound sequencer's own definition current on OPBridge so a MIDI
  // trigger fires even if the operator never once manually pressed PLAY
  // this session. See midiTrigger/ruleSync.ts.
  installMidiTriggerRuleSync();
  installModShareSync();

  // Performance mode - the ONE suite-wide switch (DEC-216). Follows OPBridge's
  // push + /health, and sends presses made in Navigate. It changes BACKGROUND
  // work only - nothing below touches what is on screen.
  installPerformanceModeSync();

  // Wake lock (SD-3) - keep the iPad awake while performance mode is on,
  // wherever it was switched. Screen Wake Lock API on a secure context, else
  // the silent-video fallback, whose muted inline autoplay iOS allows without
  // a gesture - which matters now that a switch made on the Mac can turn this
  // on. See performance/wakeLock.ts.
  createEffect(() => {
    if (performanceMode()) void acquireWakeLock();
    else releaseWakeLock();
  });
  onCleanup(() => releaseWakeLock());

  // Pre-flight (SD-13) - runs in the background when performance mode turns
  // on, and reports as a status line under the toggle in Navigate. It no
  // longer opens the full-screen checklist: the switch must not change the
  // view (DEC-216). Settings > Pre-flight still opens it on demand.
  createEffect(on(performanceMode, (isOn, wasOn) => {
    if (isOn && !wasOn) void runAndStore();
  }));

  return (
    <div
      class="viewport-fit"
      ref={viewportFitRef}
      data-edit={isEditMode() ? 'true' : 'false'}
      data-mode={uiMode()}
      onContextMenu={(ev) => {
        // Suppress the browser's native context menu app-wide so a stray
        // right-click during a gig doesn't pop up "Reload / Inspect."
        // Exception: text inputs (where the browser menu offers paste /
        // spellcheck) keep the native menu — the item-inspector
        // right-click handlers on EditableItem call preventDefault
        // themselves before this bubbles up.
        const t = ev.target as HTMLElement | null;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
        ev.preventDefault();
      }}
    >
      {/* FIT ESCAPE NOTICE (2026-07-31). Deliberately a SIBLING of `.shell`,
          not a child: it exists precisely for the case where the shell has
          been scaled too small to read, so anything inside the shell is
          unusable by definition. Rendering it here keeps it at device scale
          always.

          The lockout this prevents: an operator picked the wrong device
          preset, the canvas scaled to 0.27, and the ONLY route back (logo ->
          Navigate -> project browser) was itself at 0.27. They had to
          physically rotate the iPad to read a menu. A rig that can strand you
          inside its own settings is worse than one that renders badly.

          BOTTOM, not top (operator, 2026-07-31): the top edge is where the
          header lives, and covering the rig's own chrome with a warning about
          the rig is its own small hostility. Dismissable for the same reason -
          an operator who has understood the problem and wants to work anyway
          is making a legitimate choice. The `!` chip in the header is what
          keeps that choice reversible. */}
      <Show when={fitMismatch() && !fitNoticeDismissed()}>
        {/* `skin` + data-skin on the element itself: `data-skin` lives on
            `.shell`, and this bar is deliberately outside it, so without
            these two it would silently render in the DEFAULT skin while the
            rig is in another one. Same trick SkinPicker + SettingsPanel use. */}
        <div class="fit-escape skin" data-skin={activeSkin()} role="alert">
          <div class="fit-escape-text">
            <strong>{Math.round(shellScale() * 100)}%</strong>
            <span>
              This project is {deviceLabel(activeProject()!.device, activeProject()!.orientation)}
              {' '}and does not fit this screen.
            </span>
          </div>
          <div class="fit-escape-actions">
            {/* Navigate from here too (2026-09-30): on a phone showing an iPad
                project the logo that opens it is scaled with everything else,
                to a target too small to hit, and this bar is the one thing on
                screen at full size. Navigate itself is full size since the
                overhaul, so it is a real way out - PANIC included. */}
            <button type="button" class="fit-escape-navigate" onClick={() => openNavigate()}>
              Navigate
            </button>
            <button
              type="button"
              onClick={() => { setBrowserInitialView('list'); setBrowserOpen(true); }}
            >
              Switch project
            </button>
            <button
              type="button"
              onClick={() => { setBrowserInitialView('new'); setBrowserOpen(true); }}
            >
              New project
            </button>
            <button
              type="button"
              class="fit-escape-dismiss"
              onClick={() => setFitNoticeDismissed(true)}
              aria-label="Dismiss"
              title="Dismiss - reopen from the ! in the header"
            >
              ×
            </button>
          </div>
        </div>
      </Show>
      <div
        // `skin` puts the DEC-121 24-token contract in scope for everything
        // inside the shell. Purely additive today: the tokens are defined
        // but nothing reads them yet, so this changes no pixels. Components
        // get repointed one kind at a time (build-order step 3), and each
        // one repointed inherits every future skin for free.
        class="shell skin"
        ref={shellElRef}
        data-skin={activeSkin()}
        data-edit={isEditMode() ? 'true' : 'false'}
        data-mode={uiMode()}
        data-bar-position={barPosition()}
        // Derived from the actual canvas px, not Project.orientation or a
        // viewport media query - `.shell` is a fixed-px box that scales via
        // `transform`, so a real @media width query would read the BROWSER
        // window, not this frame (the same vw/vh trap DEC-096 already
        // guards against). w<h here is portrait regardless of what the
        // browser chrome itself is doing.
        data-orientation={(activeCanvasSize()?.h ?? bareFrame().h) > (activeCanvasSize()?.w ?? bareFrame().w) ? 'portrait' : 'landscape'}
        data-pick-mode={pickButtonForDisplayId() ? 'true' : undefined}
        data-dim={stageDim() < 1 ? 'true' : undefined}
        data-dock-anim={dockAnim() ? 'true' : undefined}
        style={{
          width: `${activeCanvasSize()?.w ?? bareFrame().w}px`,
          height: `${activeCanvasSize()?.h ?? bareFrame().h}px`,
          transform: `scale(${shellScale()})`,
          // The Inspector's dock and Navigate's (DEC-268): a separate
          // `translate`, in screen px, so the slide never multiplies with the scale.
          translate: `${shellShift().x}px ${shellShift().y}px`,
          // Screen dim (SD-4, a Settings slider since DEC-216) - a brightness
          // scalar applied via CSS filter. Only while `data-dim` is set, so a
          // screen at 100% carries no filter (and no compositing cost) at all.
          '--stage-dim': String(stageDim()),
          // The device's status-bar / home-indicator strips (devices.ts
          // reserveOf: measured on the playing device, else the table), so a
          // desktop preview loses the same canvas the installed app does. Without them the desktop canvas was 24pt
          // taller than the iPad's and controls at the bottom were cut off on
          // stage. No project, no device, no strips.
          '--frame-reserve-top': `${frameReserve().top}px`,
          '--frame-reserve-bottom': `${frameReserve().bottom}px`,
        }}
      >
        <InstrumentIconSprite />
        <Header
        bpm={bpm}
        phase={phase}
        beat={clock.beat}
        isPlaying={isPlaying}
        transport={transport}
        nowTick={nowTick}
        isSlaved={isSlaved}
        fitAlert={() => fitMismatch() && fitNoticeDismissed()}
        onShowFitNotice={() => setFitNoticeDismissed(false)}
      />
      <main
        class="canvas-placeholder"
        onClick={(ev) => {
          // Click on the empty canvas (not on an item) deselects. Suppressed
          // right after a marquee: the drag ends with a click on the canvas,
          // and clearing there would wipe the selection it just made.
          if (marqueeJustEnded) { marqueeJustEnded = false; return; }
          // A MOUSE click on empty canvas does nothing (2026-10-16, operator:
          // on a desktop "left click shouldn't do anything in blank space
          // except close the right click menu" - the menu's own backdrop
          // does that). Esc deselects. A tap still deselects: a touch device
          // has no Esc.
          if (lastCanvasPointerType === 'mouse') return;
          const t = ev.target as HTMLElement;
          if (!t.closest('.editable-item')) clearSelection();
        }}
        onPointerDown={onCanvasPointerDown}
        onContextMenu={onCanvasContextMenu}
        onTouchStart={onCanvasTouchStart}
        onTouchMove={onCanvasTouchMove}
        onTouchEnd={onCanvasTouchEnd}
        onTouchCancel={() => { twoFinger = null; swipe = null; }}
      >
        {/* INSIDE the canvas, not a sibling of it. `.shell` is a two-row grid
            (chrome auto + canvas 1fr); a third child claimed the 1fr row and
            squeezed the canvas to nothing. It also overlays rather than
            occupying space, because a banner that took a row would REFLOW
            every control the moment the link dropped - moving the operator's
            buttons mid-song is a worse failure than the one it reports. */}
        <BridgeLostBanner />
        <Show
          when={activeCanvasSize()}
          fallback={
            <div class="placeholder">
              <div class="placeholder-title">No project open</div>
              <div class="placeholder-sub">Tap the logo, then pick a project or start a new one.</div>
            </div>
          }
        >
          <ScaledCanvas pageAnimDirection={pageAnimDirection()}>
            <For each={layoutItems()}>
              {(item) => (
                <EditableItem item={item}>
                  {item.kind === 'button' ? (
                    <FireButton item={item} />
                  ) : item.kind === 'lfo' ? (
                    <SineLfo item={item} phase={phase} beatsPerBar={beatsPerBar} />
                  ) : item.kind === 'buttongrid' ? (
                    <ButtonGrid item={item} beat={clock.beat} bpm={bpm} playing={activeIsPlaying} />
                  ) : item.kind === 'streamdeck' ? (
                    <StreamDeckButton item={item} />
                  ) : item.kind === 'macro' ? (
                    <MacroItemTile item={item} />
                  ) : item.kind === 'sequencer' ? (
                    <SequencerGrid
                      item={item}
                    />
                  ) : item.kind === 'statusLed' ? (
                    <StatusLed item={item} />
                  ) : item.kind === 'fader' ? (
                    <Fader item={item} />
                  ) : item.kind === 'radial' ? (
                    <Radial item={item} />
                  ) : item.kind === 'label' ? (
                    <Label item={item} />
                  ) : item.kind === 'xypad' ? (
                    <XYPad item={item} />
                  ) : item.kind === 'timelineLauncher' ? (
                    <TimelineLauncher item={item} beat={clock.beat} beatsPerBar={beatsPerBar} bpm={bpm} />
                  ) : item.kind === 'piano' ? (
                    <Piano item={item} />
                  ) : item.kind === 'transpose' ? (
                    <Transpose item={item} />
                  ) : item.kind === 'capo' ? (
                    <Capo item={item} />
                  ) : item.kind === 'recQuant' ? (
                    <RecQuant item={item} />
                  ) : item.kind === 'abletonRow' ? (
                    <AbletonRow item={item} />
                  ) : item.kind === 'sceneCopy' ? (
                    <SceneCopy item={item} />
                  ) : item.kind === 'wavesAutotune' ? (
                    <WavesAutotune item={item} />
                  ) : item.kind === 'mixer' ? (
                    <Mixer item={item} />
                  ) : item.kind === 'presetChange' ? (
                    <PresetChange item={item} />
                  ) : item.kind === 'snapshotChange' ? (
                    <SnapshotChange item={item} />
                  ) : item.kind === 'display' ? (
                    <Display item={item} bpm={bpm} isSlaved={isSlaved} sig={activeSig} isPlaying={activeIsPlaying} beat={clock.beat} />
                  ) : item.kind === 'rigStateButton' ? (
                    <RigStateButton item={item} />
                  ) : item.kind === 'lightColor' ? (
                    <LightColorButton item={item} />
                  ) : item.kind === 'lightLevel' ? (
                    <LightLevelFader item={item} />
                  ) : item.kind === 'lightFx' ? (
                    <LightFxButton item={item} />
                  ) : item.kind === 'blackout' ? (
                    <BlackoutButton item={item} />
                  ) : item.kind === 'opvisualTrigger' ? (
                    <OpVisualTrigger item={item} />
                  ) : item.kind === 'opvisualGrid' ? (
                    <OpVisualGrid item={item} />
                  ) : item.kind === 'audioPads' ? (
                    <AudioPads item={item} />
                  ) : (
                    <div class="item-placeholder" data-kind={item.kind}>
                      {item.kind.toUpperCase()}
                    </div>
                  )}
                </EditableItem>
              )}
            </For>
            {/* Marquee box. Rendered INSIDE ScaledCanvas so its coordinates
                are canvas design-space, the same space `idsInsideMarquee`
                hit-tests in - a viewport-space box would drift the moment
                the shell scales to fit. */}
            {/* Snap guides and the settle outline while a control is dragged
                or resized (2026-10-10). Canvas design px, like the marquee.
                <Index>, not <For> (2026-10-10, "needs to be butter smooth"):
                the overlay is a NEW object every pointer move, and <For> keys
                by identity, so it tore down and rebuilt every guide each move -
                a DOM insert inside the canvas, which re-ran its :has() rules
                over every control (measured: _probe_drag_cost). <Index> keeps
                the elements and moves them. */}
            <Show when={dragOverlay()}>
              {(o) => (
                <>
                  <Index each={o().ghosts}>
                    {(g) => (
                      <div class="drag-ghost" style={{ left: `${g().x}px`, top: `${g().y}px`, width: `${g().w}px`, height: `${g().h}px` }} />
                    )}
                  </Index>
                  <Index each={o().guides}>
                    {(g) => (
                      <div
                        class="snap-guide"
                        data-axis={g().axis}
                        data-gap={g().gap ? 'true' : undefined}
                        style={g().axis === 'x'
                          ? { left: `${g().pos}px`, top: `${g().from}px`, height: `${g().to - g().from}px` }
                          : { top: `${g().pos}px`, left: `${g().from}px`, width: `${g().to - g().from}px` }}
                      />
                    )}
                  </Index>
                </>
              )}
            </Show>
            <Show when={marquee()}>
              {(m) => (
                <div
                  class="marquee-box"
                  style={{
                    left: `${Math.min(m().x0, m().x1)}px`,
                    top: `${Math.min(m().y0, m().y1)}px`,
                    width: `${Math.abs(m().x1 - m().x0)}px`,
                    height: `${Math.abs(m().y1 - m().y0)}px`,
                  }}
                />
              )}
            </Show>
          </ScaledCanvas>
        </Show>
      </main>

      {/* Mount node for iPad-bound modals. Because .shell carries a
          transform, this node's fixed-position descendants fix to the
          iPad frame (not the viewport) and scale with the shell — a
          faithful iPad preview. Zero-size + high z so it blocks nothing
          yet stacks its modals above shell chrome. See layout/shellPortal.ts. */}
      <div class="shell-portal" ref={setShellPortal} />

      {/* With no project the browser is forced open at z 100, above Connect
          (z 30). While first-run Connect is open it steps aside, and comes
          back (at the form, welcome done) when Connect closes. */}
      <Show when={(browserOpen() || !activeProject()) && !(!activeProject() && connectOpen())}>
        <ProjectBrowser
          dismissible={!!activeProject()}
          onClose={() => setBrowserOpen(false)}
          initialView={projects().length === 0 && !welcomeDone() ? 'welcome' : browserInitialView()}
          // Closing the browser as well matters when the welcome view was
          // opened from Navigate's "Getting started": with an active project
          // the browser stays mounted at z 100, above Connect (z 30). On first
          // run the browser is forced by `!activeProject()` and steps aside
          // on its own, so this changes nothing there.
          onConnect={() => { setWelcomeDone(true); setBrowserOpen(false); openConnect(); }}
        />
      </Show>

      {/* Page manager - lifted out of PagesPills (2026-09-30), which renders
          nothing for a one-page project, so Navigate's Pages card can open it
          for any project. Inside the shell like every other DEC-096 modal. */}

      <Show when={macrosEditorOpen()}>
        <MacrosEditor onClose={closeMacrosEditor} />
      </Show>

      <Show when={matrixViewerOpen()}>
        <MatrixViewer onClose={closeMatrixViewer} />
      </Show>


      {/* Connect popup — the OPBridge pairing surface in its own modal.
          Opened by the header OPBridge pill or the Settings → Connect
          shortcut. Mounted AFTER SettingsPanel so it stacks on top when
          both are open (same .matrix-overlay z-index; later DOM wins). */}

      {/* Connected Devices — the guitar-effects pedal registry (DEC-152),
          off the logo/Navigate menu since 2026-08-04 (was a Settings
          section). */}

      {/* Rig States (PLAN.md QUEUED "Rig States") - the library manager,
          off the logo menu next to Timelines/Matrix/Connected Devices. */}

      {/* Lights (2026-09-17) - the operator's rig, and OPButtons's only write
          path into it: add, name, address, group and remove. Reachable from
          the logo menu and from any lighting control's Inspector, because the
          moment you want a group is the moment you are aiming a control at
          one. Started as a groups-only panel and absorbed the patch surface
          rather than growing a sibling that listed the same lights. */}
      {/* Helix preset picker - opened from a Guitar FX Preset's Inspector,
          mounted here so the Inspector steps aside while it is up (it sat
          under the Inspector in portrait when the Inspector mounted it). */}
      <HelixPresetPickerHost />

      {/* Traffic Monitor (2026-08-11) — diagnostic modal, opened from
          Settings. See settings/store.ts's openTrafficMonitor/
          closeTrafficMonitor (they drive opbridge/trafficMonitor.ts's
          connect()/disconnect() directly) and TrafficMonitorPanel.tsx. */}
      <Show when={trafficMonitorOpen()}>
        <TrafficMonitorPanel />
      </Show>

      {/* Bearer-revoked prompt (Phase B). OPBridge ships no revoke push, so this
          fires when the resync GET 401s. "Re-pair now" reuses the normal Connect
          flow; dismissing leaves the badge in its alert state. */}
      <Show when={revokePromptOpen()}>
        <ConfirmChoice
          title="Access revoked"
          message="OPBridge no longer accepts this device's key. Re-pair to reconnect."
          options={[{ key: 'repair', label: 'Re-pair now', tone: 'primary' }]}
          onChoose={() => { setRevokePromptOpen(false); openConnect(); }}
          onCancel={() => setRevokePromptOpen(false)}
        />
      </Show>

      <Show when={paletteOpen()}>
        <ItemPalette onClose={() => { setPaletteOpen(false); setSpawnPoint(null); }} />
      </Show>

      {/* Canvas right-click menu — page-level actions (Edit Page / + Item)
          that used to be top-bar buttons. Portalled out of the scaled shell
          so `position: fixed` at the cursor resolves to the viewport, not
          the transformed shell. Backdrop / second right-click / Esc closes. */}
      <Show when={canvasMenu()}>
        {(pos) => (
          <Portal>
            <div
              class="canvas-ctx-backdrop"
              onClick={closeCanvasMenu}
              onContextMenu={(e) => { e.preventDefault(); closeCanvasMenu(); }}
            />
            <div class="canvas-ctx-menu" style={{ left: `${pos().x}px`, top: `${pos().y}px` }}>
              <button
                type="button"
                class="canvas-ctx-item"
                data-active={isEditMode() ? 'true' : 'false'}
                onClick={() => { toggleEditMode(); closeCanvasMenu(); }}
              >
                Edit Page{isEditMode() ? ' ✓' : ''}
              </button>
              <button
                type="button"
                class="canvas-ctx-item"
                onClick={() => {
                  // Add HERE: the next control goes where the menu was opened.
                  setSpawnPoint(canvasMenuAt());
                  setPaletteOpen(true);
                  closeCanvasMenu();
                }}
              >
                + Item
              </button>
              <button
                type="button"
                class="canvas-ctx-item"
                onClick={() => { addPage(); closeCanvasMenu(); }}
              >
                + Page
              </button>
              {/* The page you are on, renamed right here (2026-10-16). The
                  row becomes its name box: Enter keeps it, Esc leaves it. */}
              <Show when={activePage()}>
                {(pg) => (
                  <Show
                    when={renamingPageInMenu()}
                    fallback={
                      <button type="button" class="canvas-ctx-item" onClick={() => setRenamingPageInMenu(true)}>
                        Rename this page
                      </button>
                    }
                  >
                    <input
                      class="canvas-ctx-name"
                      value={pg().name}
                      aria-label="Page name"
                      ref={(el) => queueMicrotask(() => { el.focus(); el.select(); })}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const v = e.currentTarget.value;
                          if (v.trim() && v !== pg().name) renamePage(pg().id, v);
                          closeCanvasMenu();
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          closeCanvasMenu();
                        }
                      }}
                    />
                  </Show>
                )}
              </Show>
              {/* Paste lives here as well as on the item menu, and it is not
                  scope creep: Cut removes the item you right-clicked, so
                  without a paste target on EMPTY canvas a cut on the only
                  item in a fresh page would be unrecoverable except by undo.
                  Present only when the clipboard holds something, same rule
                  as the item menu. */}
              <Show when={hasItemClipboard()}>
                <button
                  type="button"
                  class="canvas-ctx-item"
                  onClick={() => { announceAdded(pasteItems(canvasMenuAt() ?? undefined).map((it) => it.id)); closeCanvasMenu(); }}
                >
                  Paste
                </button>
              </Show>
            </div>
          </Portal>
        )}
      </Show>

      {/* Item right-click menu — Inspect / Move / Cut / Copy / Paste /
          Duplicate / Delete. Body-portalled from inside the component for the
          same reason the canvas menu is: it anchors to a measured viewport
          point, which a transformed ancestor would break. */}
      <Show when={itemMenuTarget()}>
        {(state) => <ItemContextMenu state={state()} />}
      </Show>

      {/* A file dragged where no drop zone takes it: where audio goes (navigate/browserGuards.ts). */}
      <DropCatch />

      {/* The timeline editor is v2; the first one stays as the Legacy timeline
          (DEC-240), one click away in v2's toolbar. */}
      <Show when={timelineEditorOpen() && timelineEditorKind() === 'v2'}>
        <TimelineV2 beat={clock.beat} beatsPerBar={beatsPerBar} bpm={bpm} slaved={isSlaved} />
      </Show>
      <Show when={timelineEditorOpen() && timelineEditorKind() === 'legacy'}>
        <TimelineEditor beat={clock.beat} beatsPerBar={beatsPerBar} />
      </Show>
      </div>
      {/* Inspector — sibling of .shell so it can position itself in the
          workspace dead-zone (desktop) or as a bottom-sheet overlay
          (iPad). Lives outside the scaled shell so its size is in viewport
          pixels, not target pixels. */}
      {/* `inspectorItem()`, not `selectedItem()`: the panel binds only when
          EXACTLY ONE item is highlighted. Its fields edit one item, and
          showing them over a five-item selection whose every menu action
          hits all five is how you edit the wrong control. Multi-select
          therefore closes it; the menu's Inspect row re-opens it by
          collapsing the selection back to one. */}
      <Show when={anyModalOpen() ? null : inspectorItem()}>
        {(item) => <InspectorPanel item={item()} />}
      </Show>
      {/* Navigate - the launcher. Body-mounted at the REAL viewport (a DEC-096
          exception, like Matrix and Timelines), so it fits the device in
          hand rather than the project's frame: inside the shell an iPad
          project on an iPhone drew it at 33%, 6 px text. See Navigate.tsx. */}
      {/* Docked on a desktop (DEC-268) it stays mounted: its header and
          sidebar are the app's chrome, and the canvas is its first section. */}
      <Show when={navigateOpen() || navigateDocked()}>
        <Navigate
          onOpenProjects={() => { setBrowserInitialView('list'); setBrowserOpen(true); }}
          onNewProject={() => { setBrowserInitialView('new'); setBrowserOpen(true); }}
          onAddControl={() => { if (!isEditMode()) toggleEditMode(); setPaletteOpen(true); }}
          onGettingStarted={() => { setBrowserInitialView('welcome'); setBrowserOpen(true); }}
        />
      </Show>
      {/* Waves Autotune Matrix-conflict prompt. Always mounted so it can
          fire on add (not a selection event) as well as channel change. */}
      <WavesConflictModal />
      {/* Pre-flight checklist (SD-13) — opens on Performance Mode entry + on demand.
          Shell-portalled inside PreflightPanel. */}
      {/* Transient toasts (panic result, etc.) — shell-portalled so they
          stay iPad-bound (DEC-096) and stack above every modal. */}
      <ToastHost />
      <CrashPrompt />
    </div>
  );
}
